import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { GroupSheetComponent } from '../../components/group-sheet/group-sheet.component';
import { GroupEngageComponent } from '../../components/group-engage/group-engage.component';
import { CommonModule, DOCUMENT, Location } from '@angular/common';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import type { TablePageEvent } from 'primeng/table';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { SidebarModule } from 'primeng/sidebar';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import type { ParamMap } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { DeleteConfirmDialogComponent } from '../../components/delete-confirm-dialog/delete-confirm-dialog.component';
import { DeleteGroupWithIncomingDialogComponent } from '../../components/delete-group-with-incoming-dialog/delete-group-with-incoming-dialog.component';
import { ReadonlyListsComponent } from '../../components/readonly-lists/readonly-lists.component';
import { ActivityDrawerHostComponent } from '../../components/activity-drawer-host/activity-drawer-host.component';
import { SummaryCardComponent } from '../../components/summary-card/summary-card.component';
import type { ActivityDrawerDeleteRequest } from '../../components/activity-drawer-host/activity-drawer-host.component';
import type { ActivityDrawerSessionState } from '../../models/activity-drawer-session.model';
import {
  canTogglePreviewList,
  canSaveGroupDraftChanges,
  getPreviewItems,
  getPreviewMoreCount,
  hasGroupDraftChanges,
  isGroupDraftWebsiteUrlValid,
  resetDraftFromOriginal,
  restoreDraftField,
  updateDraftCoverPhoto
} from '../../utils/group-draft.util';
import { GroupsManagementApiService } from '../../../services/groups-management/groups-management-api.service';
import type {
  GroupActivityStatusTab,
  GroupManagementDetail
} from '../../../services/groups-management/groups-management-api.service';
import { GroupsManagementModalSessionService } from '../../../services/groups-management/groups-management-modal-session.service';
import { GroupsManagementPhotoService } from '../../../services/groups-management/groups-management-photo.service';
import { ManagementListDebounce } from '../../utils/management-list-debounce.util';
import { toTwoColumnOptions } from '../../utils/management-options-layout.util';
import {
  getSportIconPath,
  getSportLabelFromValue,
  getSportOptionValue
} from '../../utils/management-sport.util';
import { getManagementTableLazyState, getPreviousPageFirst } from '../../utils/management-table-state.util';
import { ManagementToastTimer } from '../../utils/management-toast-timer.util';
import { ManagementViewportScrollService } from '../../../services/shared/management-viewport-scroll.service';
import {
  DEFAULT_GROUP_COVER_URL,
  EDIT_LIST_PREVIEW_COUNT
} from '../../constants/management-ui.constants';
import type {
  EditGroupActivity,
  EditGroupDraft,
  EditGroupMember,
  EditGroupSubgroup,
  ActivityStatusCounts,
  GroupsListQuery,
  GroupsListSortField,
  GroupsModalSessionState,
  GroupsSummary,
  GroupRow,
  ManagementDeleteTarget,
  ModalEditTarget,
  SportOption,
  SportTypeConfig
} from '../../models/groups-management.model';

type SubgroupDrawerStackFrame = {
  draft: EditGroupDraft;
  original: EditGroupDraft;
  drawerScrollPosition: number;
  editingName: boolean;
  editingDescription: boolean;
  editingWebsiteUrl: boolean;
  editingSport: boolean;
  coverPhotoMenuVisible: boolean;
  coverPhotoFailed: boolean;
  saveSuccessVisible: boolean;
  discardSuccessVisible: boolean;
  membersExpanded: boolean;
  subgroupsExpanded: boolean;
  activitiesExpanded: boolean;
};

const EMPTY_GROUPS_SUMMARY: GroupsSummary = {
  totalGroups: 0,
  groupsWithoutAdmins: 0,
  groupsGrowth: 0,
  totalMembers: 0,
  totalActivities: 0,
  topSportType: '-',
  topSportPercentage: 0
};

@Component({
  selector: 'bo-groups-management',
  standalone: true,
  imports: [
    GroupSheetComponent,
    GroupEngageComponent,
    TranslateModule,
    CommonModule,
    TableModule,
    InputGroupModule,
    InputGroupAddonModule,
    DropdownModule,
    ButtonModule,
    AvatarModule,
    InputTextModule,
    FormsModule,
    RippleModule,
    TooltipModule,
    SidebarModule,
    InputTextareaModule,
    DeleteConfirmDialogComponent,
    DeleteGroupWithIncomingDialogComponent,
    ReadonlyListsComponent,
    ActivityDrawerHostComponent,
    SummaryCardComponent
  ],
  templateUrl: './groups-management.component.html',
  styleUrls: ['./groups-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GroupsManagementComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() drawerOnly = false;
  @Input() embeddedInUserDetail = false;
  @Input() drawerBaseZIndex = 1000;

  @Output() nestedDrawerVisibilityChange = new EventEmitter<void>();

  get showGroupDrawerBackArrow(): boolean {
    return this.embeddedInUserDetail || this.drawerOnly;
  }

  get groupDrawerBackLabel(): string {
    return this.showGroupDrawerBackArrow
      ? 'Back to user details'
      : 'Close group detail';
  }

  @ViewChild(ActivityDrawerHostComponent) private activityDrawerHost?: ActivityDrawerHostComponent;
  @ViewChild('groupsTable') private groupsTable?: Table;

  groups: GroupRow[] = [];
  totalRecords = 0;
  tableFirst = 0;
  tableRows = 10;
  tableLoading = false;
  tableSortField?: GroupsListSortField;
  tableSortOrder?: 'asc' | 'desc';
  get tableSortOrderValue(): number {
    return this.tableSortOrder === 'asc' ? 1 : this.tableSortOrder === 'desc' ? -1 : 0;
  }

  private loadedGroupsSnapshot: GroupRow[] = [];
  private groupsListLoaded = false;
  private viewInitialized = false;
  private modalRouteRestoreCompleted = false;
  private readonly membersByDraftKey = new Map<string, EditGroupMember[]>();
  private readonly subgroupsByDraftKey = new Map<string, EditGroupSubgroup[]>();
  private readonly activitiesByDraftKey = new Map<string, EditGroupActivity[]>();
  private readonly memberTotalsByDraftKey = new Map<string, number>();
  private readonly subgroupTotalsByDraftKey = new Map<string, number>();
  private readonly activityTotalsByDraftKey = new Map<string, number>();
  private readonly activityStatusCountsByDraftKey = new Map<string, ActivityStatusCounts>();
  private readonly readonlyListRequestsByDraftKey = new Set<string>();
  private readonly memberListRequestsByDraftKey = new Set<string>();
  private readonly subgroupListRequestsByDraftKey = new Set<string>();
  private readonly activityListRequestsByDraftKey = new Set<string>();
  private readonly fullMembersLoadedByDraftKey = new Set<string>();
  private readonly fullSubgroupsLoadedByDraftKey = new Set<string>();
  private readonly fullActivitiesLoadedByDraftKey = new Set<string>();
  private readonly savedCoverPhotoOverridesByGroupId = new Map<string, string | null>();
  private groupsRequestSubscription: Subscription | null = null;
  private lastPageEventKey = '';
  private shouldIgnoreInitialLazyLoad = true;
  private readonly operationSubscriptions = new Subscription();
  private readonly searchReload = new ManagementListDebounce();
  private readonly toastDismiss = new ManagementToastTimer();
  private initialPageScrollRestored = false;
  private scrollRestorationPending = false;
  private pendingPageScrollRestore = 0;
  private hasEarlyPageScrollLock = false;
  private postRestoreScrollGuardFrame: number | null = null;
  private postRestoreScrollGuardUntil = 0;
  private postRestoreScrollGuardLockActive = false;
  private postRestoreTargetScrollY = 0;

  summary: GroupsSummary = EMPTY_GROUPS_SUMMARY;
  summaryLoading = true;
  sportTypes = [{ label: 'All sport types', value: 'all' }];
  selectedSportType = 'all';

  editGroupSportOptions: SportOption[] = [];
  sportTypeConfigs: SportTypeConfig[] = [];
  editListPreviewCount = EDIT_LIST_PREVIEW_COUNT;
  private readonly subgroupListPreviewCount = 2;
  private readonly activityListPreviewCount = 2;
  private groupActivityStatus: GroupActivityStatusTab = 'incoming';
  private subgroupActivityStatus: GroupActivityStatusTab = 'incoming';
  defaultCoverPhotoUrl = DEFAULT_GROUP_COVER_URL;

  searchQuery = '';
  cityFilter = '';
  withoutAdminsOnly = false;
  privateOnly = false;
  showFilter = false;
  deleteDialogVisible = false;
  deleteDialogClosing = false;
  skipDeleteDialogEnterAnimation = false;
  deleteConfirmed = false;
  groupToDelete: ManagementDeleteTarget | null = null;
  deleteTargetType: 'group' | 'subgroup' | 'activity' = 'group';
  deleteTargetName = '';

  deleteGroupWithIncomingDialogVisible = false;
  deleteGroupWithIncomingDialogClosing = false;
  skipDeleteGroupWithIncomingDialogEnterAnimation = false;
  deleteGroupWithIncomingGroupName = '';
  deleteGroupWithIncomingCount = 0;
  deleteGroupWithIncomingConfirmed = false;

  editingName = false;
  editingDescription = false;
  editingWebsiteUrl = false;
  editingSport = false;
  openedInEditMode = false;
  selectedGroup: GroupRow | null = null;
  private pageScrollPosition = 0;
  private modalScrollPosition = 0;
  private groupDrawerScrollPosition = 0;
  private isReloadNavigation = false;
  private subgroupDrawerScrollPosition = 0;
  private modalViewportLocked = false;
  private readonly modalScrollKeys = new Set(['ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp', ' ']);
  private readonly preventBackgroundPointerScroll = (event: Event) => {
    if (!this.isAnyModalVisible()) {
      return;
    }

    if (this.viewportScroll.isDrawerScrollEvent(event, this.isDeleteDialogActive())) {
      return;
    }

    event.preventDefault();
  };
  private readonly preventBackgroundKeyboardScroll = (event: KeyboardEvent) => {
    if (
      this.isAnyModalVisible() &&
      this.modalScrollKeys.has(event.key) &&
      !this.isTopDrawerBodyTarget(event.target)
    ) {
      event.preventDefault();
    }
  };
  private outsideDrawerDismissListenerAttached = false;
  private groupDrawerClosing = false;
  private subgroupDrawerClosing = false;
  private groupDrawerCloseFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private subgroupDrawerCloseFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly drawerCloseAnimationMs = 200;
  private readonly onOutsideDrawerPointerDown = (event: Event) => {
    if (!this.shouldDismissGroupDrawerOnOutsideClick()) {
      return;
    }

    if (event instanceof PointerEvent && event.button !== 0) {
      return;
    }

    if (this.isEventInsideGroupDrawerPanel(event)) {
      return;
    }

    this.onDrawerBackdropClick();
    event.preventDefault();
    event.stopPropagation();
  };
  private readonly onOutsideDrawerClick = (event: Event) => {
    if (!this.shouldDismissGroupDrawerOnOutsideClick()) {
      return;
    }

    if (this.isEventInsideGroupDrawerPanel(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  editDrawerVisible = false;
  editDraft: EditGroupDraft | null = null;
  editOriginal: EditGroupDraft | null = null;
  skipEditDrawerEnterAnimation = false;
  
  subgroupDrawerVisible = false;
  private subgroupDrawerStack: SubgroupDrawerStackFrame[] = [];
  subgroupDraft: EditGroupDraft | null = null;
  subgroupOriginal: EditGroupDraft | null = null;
  skipSubgroupDrawerEnterAnimation = false;
  subgroupEditingName = false;
  subgroupEditingDescription = false;
  subgroupEditingWebsiteUrl = false;
  subgroupEditingSport = false;
  subgroupCoverPhotoMenuVisible = false;
  subgroupCoverPhotoFailed = false;
  subgroupSaveSuccessVisible = false;
  subgroupDiscardSuccessVisible = false;
  subgroupMembersExpanded = false;
  subgroupSubgroupsExpanded = false;
  subgroupActivitiesExpanded = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private groupsApi: GroupsManagementApiService,
    private modalSession: GroupsManagementModalSessionService,
    private photoService: GroupsManagementPhotoService,
    private viewportScroll: ManagementViewportScrollService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.viewportScroll.setManualHistoryScrollRestoration();
    this.isReloadNavigation = this.viewportScroll.isReloadNavigation();
  }

  coverPhotoMenuVisible = false;
  coverPhotoFailed = false;
  membersExpanded = false;
  subgroupsExpanded = false;
  activitiesExpanded = false;
  saveSuccessVisible = false;
  discardSuccessVisible = false;
  deleteSuccessMessage = '';
  operationErrorMessage = '';
  readonly sportIconPath = (sport: string) => this.getSportIconPath(sport);
  readonly activityLevelLabel = (activity: EditGroupActivity) => {
    const level = activity.level?.trim().toLowerCase();
    return !level || level === 'all' || level === 'all levels' ? 'All levels' : `Level ${level.match(/\d+/)?.[0] ?? level}`;
  };
  readonly activityTypeLabel = (activity: EditGroupActivity) =>
    (activity.activityType ?? '').replace(/\s+-\s+/g, ' • ');

  readonly apiActivityTypeLabel = (activity: EditGroupActivity) =>
    (activity.activityType ?? '').replace(/\s+-\s+/g, ' - ');


  ngOnInit() {
    if (this.drawerOnly) {
      this.releasePendingPageScrollLock();
    } else {
      this.applyStoredPageState();
      this.prepareEarlyPageScrollLock();
      this.document.documentElement.classList.add('groups-page-scroll-stable');
      this.operationSubscriptions.add(this.router.events.subscribe(event => {
        if (event instanceof NavigationStart && !event.url.startsWith('/groups')) {
          this.modalSession.clearPageState();
          this.modalSession.clear();
        }
      }));
    }
    const restoringModal = !this.drawerOnly && this.hasModalRouteQuery();
    this.loadReferenceData(!restoringModal);
    if (!this.drawerOnly) {
      this.loadGroups(!restoringModal);
    }
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    if (this.drawerOnly) {
      this.cdr.markForCheck();
      return;
    }
    this.tryRestoreModalStateFromRoute();
    if (this.isAnyModalVisible()) {
      this.initialPageScrollRestored = true;
    }
    this.cdr.markForCheck();
  }

  openGroupFromExternal(group: {
    id: string;
    title?: string;
    sport?: string;
  }) {
    const row: GroupRow = {
      id: group.id,
      name: group.title ?? '',
      admin: { name: '-', email: '' },
      sport: group.sport ?? null,
      subgroups: 0,
      members: 0,
      activities: 0,
      createdOn: '',
    };
    this.activityDrawerHost?.close();
    this.openEditDrawer(row, false, false);
    this.cdr.markForCheck();
  }

  closeNestedDrawersFromParent() {
    this.closeNestedDrawers();
  }

  cleanupModalArtifactsIfIdle() {
    this.scheduleIdleModalCleanup();
  }

  ngOnDestroy() {
    this.clearGroupDrawerCloseFallback();
    this.clearSubgroupDrawerCloseFallback();
    this.syncOutsideDrawerDismissListener(false);
    this.document.removeEventListener('pointerdown', this.onOutsideDrawerPointerDown, true);
    this.document.removeEventListener('click', this.onOutsideDrawerClick, true);
    this.stopPostRestoreScrollGuard();
    this.releasePendingPageScrollLock();
    this.unlockModalViewport();
    if (!this.drawerOnly) {
      this.document.documentElement.classList.remove('groups-page-scroll-stable');
    }
    this.document.documentElement.classList.remove('groups-scroll-restoring');
    this.groupsRequestSubscription?.unsubscribe();
    this.operationSubscriptions.unsubscribe();
    this.searchReload.clear();
    this.clearToastTimeout();
  }

  toggleFilter() {
    this.showFilter = !this.showFilter;
    this.persistPageState();
  }

  @HostListener('window:scroll')
  onPageScroll() {
    if (!this.initialPageScrollRestored || this.isAnyModalVisible()) {
      return;
    }

    this.pageScrollPosition = this.document.defaultView?.scrollY ?? 0;
    this.persistPageState();
  }

  @HostListener('window:beforeunload')
  @HostListener('window:pagehide')
  onBeforePageUnload() {
    const scrollY = this.isAnyModalVisible()
      ? this.modalScrollPosition
      : this.postRestoreScrollGuardLockActive
        ? this.postRestoreTargetScrollY
        : this.document.defaultView?.scrollY ?? this.pageScrollPosition;
    this.pageScrollPosition = scrollY;
    this.persistPageState();
  }

  get deleteTargetLabel(): 'group' | 'subgroup' | 'activity' {
    return this.deleteTargetType;
  }

  /** Standalone /groups page only — not when nested in user management. */
  private shouldSyncRouteUrl(): boolean {
    return !this.drawerOnly && !this.embeddedInUserDetail;
  }

  private syncModeUrl() {
    if (!this.shouldSyncRouteUrl()) {
      return;
    }

    const queryParams: Record<string, string> = {};

    if (this.editDrawerVisible && this.editDraft?.id) {
      queryParams['group'] = this.editDraft.id;
    }

    if (this.subgroupDrawerVisible && this.subgroupDraft?.id) {
      queryParams['subgroup'] = this.subgroupDraft.id;
    }

    const activityDraft = this.getActivityDraft();
    if (this.isActivityDrawerVisible() && activityDraft?.id) {
      queryParams['activity'] = activityDraft.id;
    }

    const editTarget = this.getActiveEditTarget();
    if (editTarget) {
      queryParams['mode'] = 'edit';
      queryParams['edit'] = editTarget;
    }

    if (this.isDeleteDialogActive()) {
      if (!queryParams['group'] && this.groupToDelete) {
        queryParams['group'] = this.groupToDelete.id;
      }
      queryParams['delete'] = this.deleteTargetType;
    }

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/groups'], {
        queryParams: Object.keys(queryParams).length ? queryParams : undefined
      })
    );

    const scrollY = this.pageScrollPosition;
    this.location.replaceState(url);
    this.persistModalSession(url);
    if (!this.isAnyModalVisible()) {
      this.restoreListScrollAfterUrlChange(scrollY);
    }
  }

  private restoreListScrollAfterUrlChange(scrollY: number) {
    if (scrollY <= 0) {
      return;
    }

    this.pageScrollPosition = scrollY;
    this.document.defaultView?.requestAnimationFrame(() => {
      this.document.defaultView?.scrollTo(0, scrollY);
      this.document.defaultView?.requestAnimationFrame(() => {
        this.document.defaultView?.scrollTo(0, scrollY);
      });
    });
  }

  private tryRestoreModalStateFromRoute() {
    if (!this.viewInitialized || this.modalRouteRestoreCompleted) {
      return;
    }

    const storedState = this.readModalSession();
    const canRestoreStoredState =
      storedState?.url === this.location.path() &&
      this.hasVisibleModalInSession(storedState);
    if (this.hasModalRouteQuery() && !canRestoreStoredState && !this.groupsListLoaded) {
      return;
    }

    this.modalRouteRestoreCompleted = true;
    this.restoreModalStateFromRoute(storedState);
  }

  private restoreModalStateFromRoute(storedState = this.readModalSession()) {
    if (!this.hasModalRouteQuery()) {
      this.clearModalSession();
      return;
    }

    if (storedState?.url === this.location.path() && this.hasVisibleModalInSession(storedState)) {
      if (this.isReloadNavigation) {
        storedState.deleteDialogVisible = false;
        storedState.deleteConfirmed = false;
        storedState.groupToDelete = null;
        storedState.deleteTargetType = 'group';
        storedState.deleteTargetName = '';
      }
      this.applyStoredModalSession(storedState);
      this.hydrateRestoredModalDetails();
      
      if (this.isReloadNavigation) {
        this.syncModeUrl();
      }
      return;
    }

    if (!this.restoreModalStateFromUrl()) {
      this.resetFailedModalRestore();
      this.clearModalSession();
      this.location.replaceState('/groups');
      return;
    }

    this.hydrateRestoredModalDetails();
  }

  private hasModalRouteQuery(): boolean {
    const queryParamMap = this.route.snapshot.queryParamMap;
    return (
      queryParamMap.has('group') ||
      queryParamMap.has('subgroup') ||
      queryParamMap.has('activity') ||
      queryParamMap.has('activities') ||
      queryParamMap.has('delete') ||
      queryParamMap.has('mode') ||
      queryParamMap.has('edit')
    );
  }

  private getActiveEditTarget(): ModalEditTarget | null {
    if (this.isActivityDrawerVisible() && this.activityDrawerHost?.isEditActionMode()) {
      return 'activity';
    }

    if (this.subgroupDrawerVisible && this.isSubgroupEditActionMode()) {
      return 'subgroup';
    }

    if (this.editDrawerVisible && (this.openedInEditMode || this.isGroupEditActionMode())) {
      return 'group';
    }

    return null;
  }

  isGroupDrawerCovered(): boolean {
    return this.subgroupDrawerVisible || this.isActivityDrawerVisible() || this.isDeleteDialogActive();
  }

  isSubgroupDrawerCovered(): boolean {
    return this.isActivityDrawerVisible() || this.isDeleteDialogActive();
  }

  isActivityDrawerCovered(): boolean {
    return this.isDeleteDialogActive();
  }

  isDeleteDialogActive(): boolean {
    return this.deleteDialogVisible || this.deleteDialogClosing;
  }

  isDeleteGroupWithIncomingDialogActive(): boolean {
    return this.deleteGroupWithIncomingDialogVisible || this.deleteGroupWithIncomingDialogClosing;
  }

  isAnyModalVisible(): boolean {
    return this.editDrawerVisible ||
      this.subgroupDrawerVisible ||
      this.groupDrawerClosing ||
      this.subgroupDrawerClosing ||
      this.isActivityDrawerVisible() ||
      this.isDeleteDialogActive() ||
      this.isDeleteGroupWithIncomingDialogActive();
  }

  private updateModalViewportLock(restoredScrollPosition?: number) {
    if (this.hasEarlyPageScrollLock) {
      this.pendingPageScrollRestore = 0;
      this.releasePendingPageScrollLock();
    }

    if (!this.isAnyModalVisible()) {
      this.unlockModalViewport();
      this.syncGroupDrawerOutsideDismiss();
      this.notifyEmbeddedDrawerVisibilityChange();
      return;
    }

    const pendingRestoreScrollY = this.pendingPageScrollRestore > 0 ? this.pendingPageScrollRestore : null;
    const scrollY = restoredScrollPosition ??
      (this.modalViewportLocked ? this.modalScrollPosition : null) ??
      pendingRestoreScrollY ??
      this.document.defaultView?.scrollY ??
      0;
    this.modalScrollPosition = scrollY;
    this.pageScrollPosition = scrollY;
    this.persistPageState();

    if (this.modalViewportLocked) {
      this.viewportScroll.updateModalBackgroundLock(this.modalScrollPosition);
      this.syncGroupDrawerOutsideDismiss();
      this.notifyEmbeddedDrawerVisibilityChange();
      return;
    }

    this.viewportScroll.lockModalBackground(
      this.modalScrollPosition,
      this.preventBackgroundPointerScroll,
      this.preventBackgroundKeyboardScroll
    );
    this.modalViewportLocked = true;
    this.syncGroupDrawerOutsideDismiss();
    this.notifyEmbeddedDrawerVisibilityChange();
  }

  private notifyEmbeddedDrawerVisibilityChange() {
    if (this.drawerOnly) {
      this.nestedDrawerVisibilityChange.emit();
    }
  }

  private unlockModalViewport() {
    if (!this.modalViewportLocked) {
      return;
    }

    this.viewportScroll.unlockModalBackground(
      this.modalScrollPosition,
      this.preventBackgroundPointerScroll,
      this.preventBackgroundKeyboardScroll
    );
    this.modalViewportLocked = false;
    this.syncGroupDrawerOutsideDismiss();
  }

  private getActiveGroupDrawerElements(): HTMLElement[] {
    return Array.from(
      this.document.querySelectorAll<HTMLElement>(
        '.edit-group-sidebar.p-sidebar-active:not(.activity-detail-sidebar)'
      )
    );
  }

  private getTopActiveGroupDrawer(): HTMLElement | null {
    const activeDrawers = this.getActiveGroupDrawerElements();
    return activeDrawers.length > 0
      ? activeDrawers[activeDrawers.length - 1]
      : null;
  }

  private shouldDismissGroupDrawerOnOutsideClick(): boolean {
    return this.isDrawerBackdropVisible();
  }

  private isEventInsideGroupDrawerPanel(event: Event): boolean {
    const topDrawer = this.getTopActiveGroupDrawer();
    if (!topDrawer) {
      return false;
    }

    for (const node of event.composedPath()) {
      if (!(node instanceof Element)) {
        continue;
      }

      if (node.closest('.activity-detail-sidebar')) {
        continue;
      }

      if (topDrawer.contains(node)) {
        return true;
      }

      if (node.closest('.delete-dialog-mask')) {
        return true;
      }
    }

    return false;
  }

  private syncOutsideDrawerDismissListener(shouldListen = this.shouldDismissGroupDrawerOnOutsideClick()) {
    if (shouldListen && !this.outsideDrawerDismissListenerAttached) {
      this.document.addEventListener('pointerdown', this.onOutsideDrawerPointerDown, true);
      this.document.addEventListener('click', this.onOutsideDrawerClick, true);
      this.outsideDrawerDismissListenerAttached = true;
      return;
    }

    if (!shouldListen && this.outsideDrawerDismissListenerAttached) {
      this.document.removeEventListener('pointerdown', this.onOutsideDrawerPointerDown, true);
      this.document.removeEventListener('click', this.onOutsideDrawerClick, true);
      this.outsideDrawerDismissListenerAttached = false;
    }
  }

  private syncGroupDrawerOutsideDismiss() {
    this.syncOutsideDrawerDismissListener(this.shouldDismissGroupDrawerOnOutsideClick());
  }

  private isTopDrawerBodyTarget(target: EventTarget | null): boolean {
    return this.viewportScroll.isTopDrawerBodyTarget(target, this.isDeleteDialogActive());
  }

  private toRouteSlug(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, '-');
  }

  isDrawerBackdropVisible(): boolean {
    if (this.isDeleteDialogActive() || this.isActivityDrawerVisible()) {
      return false;
    }

    return this.editDrawerVisible ||
      this.subgroupDrawerVisible ||
      this.groupDrawerClosing ||
      this.subgroupDrawerClosing;
  }

  onDrawerBackdropClick(): void {
    if (this.subgroupDrawerVisible) {
      this.closeSubgroupDrawer();
      return;
    }

    if (this.editDrawerVisible) {
      this.closeEditDrawer();
      return;
    }

    if (this.subgroupDrawerClosing || this.groupDrawerClosing) {
      return;
    }

    this.finalizeStaleGroupDrawerDom();
  }

  private clearGroupDrawerCloseFallback() {
    if (this.groupDrawerCloseFallbackTimer === null) {
      return;
    }

    this.document.defaultView?.clearTimeout(this.groupDrawerCloseFallbackTimer);
    this.groupDrawerCloseFallbackTimer = null;
  }

  private clearSubgroupDrawerCloseFallback() {
    if (this.subgroupDrawerCloseFallbackTimer === null) {
      return;
    }

    this.document.defaultView?.clearTimeout(this.subgroupDrawerCloseFallbackTimer);
    this.subgroupDrawerCloseFallbackTimer = null;
  }

  private scheduleGroupDrawerCloseFallback() {
    this.clearGroupDrawerCloseFallback();
    this.groupDrawerCloseFallbackTimer = (this.document.defaultView?.setTimeout(() => {
      this.groupDrawerCloseFallbackTimer = null;
      this.finalizeGroupDrawerClose();
    }, this.drawerCloseAnimationMs) as unknown as ReturnType<typeof setTimeout>) ?? null;
  }

  private scheduleSubgroupDrawerCloseFallback() {
    this.clearSubgroupDrawerCloseFallback();
    this.subgroupDrawerCloseFallbackTimer = (this.document.defaultView?.setTimeout(() => {
      this.subgroupDrawerCloseFallbackTimer = null;
      this.finalizeSubgroupDrawerClose();
    }, this.drawerCloseAnimationMs) as unknown as ReturnType<typeof setTimeout>) ?? null;
  }

  private finalizeGroupDrawerClose() {
    if (this.editDrawerVisible || this.subgroupDrawerVisible) {
      this.groupDrawerClosing = false;
      return;
    }

    if (!this.groupDrawerClosing) {
      return;
    }

    const staleDrawers = this.getActiveGroupDrawerElements();
    if (!staleDrawers.length) {
      this.groupDrawerClosing = false;
      return;
    }

    staleDrawers.forEach(drawer => {
      drawer.classList.remove('p-sidebar-active', 'p-sidebar-visible');
    });
    this.groupDrawerClosing = false;
    this.onEditDrawerHidden();
    this.cdr.markForCheck();
  }

  private finalizeSubgroupDrawerClose() {
    if (this.subgroupDrawerVisible) {
      this.subgroupDrawerClosing = false;
      return;
    }

    if (!this.subgroupDrawerClosing) {
      return;
    }

    const staleDrawers = this.getActiveGroupDrawerElements();
    if (!staleDrawers.length) {
      this.subgroupDrawerClosing = false;
      return;
    }

    staleDrawers.forEach(drawer => {
      drawer.classList.remove('p-sidebar-active', 'p-sidebar-visible');
    });
    this.subgroupDrawerClosing = false;
    this.onSubgroupDrawerHidden();
    this.cdr.markForCheck();
  }

  private finalizeStaleGroupDrawerDom() {
    if (
      this.editDrawerVisible ||
      this.subgroupDrawerVisible ||
      this.groupDrawerClosing ||
      this.subgroupDrawerClosing
    ) {
      return;
    }

    const staleDrawers = this.getActiveGroupDrawerElements();
    if (!staleDrawers.length) {
      return;
    }

    staleDrawers.forEach(drawer => {
      drawer.classList.remove('p-sidebar-active', 'p-sidebar-visible');
    });
    this.updateModalViewportLock();
    this.syncModeUrl();
    this.onEditDrawerHidden();
    this.cdr.markForCheck();
  }

  isActivityDrawerVisible(): boolean {
    return !!this.activityDrawerHost?.visible;
  }

  private getActivityDraft(): EditGroupActivity | null {
    return this.activityDrawerHost?.activityDraft ?? null;
  }

  private patchActivityFields(state: GroupsModalSessionState) {
    const activitySession = this.activityDrawerHost?.getSessionState();
    if (!activitySession) {
      state.activityDrawerVisible = false;
      state.activityDraft = null;
      state.activityOriginal = null;
      state.activityLevel = 'all';
      state.activityOriginalLevel = 'all';
      state.activityEditingName = false;
      state.activityEditingDescription = false;
      state.activityEditingSport = false;
      state.activityEditingLevel = false;
      state.activityCoverPhotoMenuVisible = false;
      state.activityParticipantsExpanded = false;
      state.activityDrawerScrollPosition = 0;
      return;
    }

    state.activityDrawerVisible = activitySession.visible;
    state.activityDraft = activitySession.draft;
    state.activityOriginal = activitySession.original;
    state.activityLevel = activitySession.level;
    state.activityOriginalLevel = activitySession.originalLevel;
    state.activityEditingName = activitySession.editingName;
    state.activityEditingDescription = activitySession.editingDescription;
    state.activityEditingSport = activitySession.editingSport;
    state.activityEditingLevel = activitySession.editingLevel;
    state.activityCoverPhotoMenuVisible = activitySession.coverPhotoMenuVisible;
    state.activityParticipantsExpanded = activitySession.participantsExpanded;
    state.activityDrawerScrollPosition = activitySession.scrollPosition;
  }

  private toActivityDrawerSession(state: GroupsModalSessionState): Partial<ActivityDrawerSessionState> {
    return {
      visible: !!state.activityDrawerVisible && !!state.activityDraft,
      skipEnterAnimation: !!state.activityDrawerVisible,
      scrollPosition: state.activityDrawerScrollPosition ?? 0,
      draft: state.activityDraft,
      original: state.activityOriginal,
      level: state.activityLevel ?? 'all',
      originalLevel: state.activityOriginalLevel ?? state.activityLevel ?? 'all',
      editingName: !!state.activityEditingName,
      editingDescription: !!state.activityEditingDescription,
      editingSport: !!state.activityEditingSport,
      editingLevel: !!state.activityEditingLevel,
      coverPhotoMenuVisible: !!state.activityCoverPhotoMenuVisible,
      participantsExpanded: !!state.activityParticipantsExpanded
    };
  }

  onActivityDrawerSessionChange() {
    this.onModalDraftChanged();
    this.syncModeUrl();
  }

  onActivityOperationErrorChange(message: string) {
    this.operationErrorMessage = message;
    this.cdr.markForCheck();
  }

  onActivityDeleteRequested(request: ActivityDrawerDeleteRequest) {
    this.openDeleteDialog(request.deleteTarget, 'activity');
  }

  onActivityDeleted(activity: EditGroupActivity) {
    this.closeDeleteDialog();
    this.deleteSuccessMessage = `Activity "${activity.name}" has been moved to Deleted items`;
    this.scheduleToastDismiss(() => {
      this.deleteSuccessMessage = '';
    });
    this.removeActivityFromLocalState(activity);
    this.loadReferenceData(false);
    this.loadGroups(false);

    // Refresh group/subgroup detail to reload activities list
    const draft = this.subgroupDrawerVisible ? this.subgroupDraft : this.editDraft;
    if (draft?.id) {
      if (this.subgroupDrawerVisible) {
        this.refreshUpdatedSubgroupDetail(draft.id!);
      } else {
        this.refreshUpdatedGroupDetail(draft.id!);
      }
    }

    this.updateModalViewportLock();
    this.cdr.markForCheck();
  }

  onActivityCancelled(activity: EditGroupActivity) {
    this.deleteSuccessMessage = `Activity "${activity.name}" has been cancelled`;
    this.scheduleToastDismiss(() => {
      this.deleteSuccessMessage = '';
    });
    this.removeActivityFromLocalState(activity);
    this.loadReferenceData(false);
    this.loadGroups(false);

    // Refresh group/subgroup detail to reload activities list
    const draft = this.subgroupDrawerVisible ? this.subgroupDraft : this.editDraft;
    if (draft?.id) {
      if (this.subgroupDrawerVisible) {
        this.refreshUpdatedSubgroupDetail(draft.id!);
      } else {
        this.refreshUpdatedGroupDetail(draft.id!);
      }
    }

    this.updateModalViewportLock();
    this.cdr.markForCheck();
  }

  private updateActivityStatusCountsForCancel(activity: EditGroupActivity) {
    const draft = this.subgroupDrawerVisible ? this.subgroupDraft : this.editDraft;
    if (!draft) {
      return;
    }

    const cacheKey = this.getDraftCacheKey(draft);
    const currentCounts = this.activityStatusCountsByDraftKey.get(cacheKey) ?? { incoming: 0, completed: 0, cancelled: 0 };
    const oldStatus = this.toActivityStatusCountKey(activity.status);

    // Decrease count for old status (e.g., incoming)
    const updatedCounts = {
      ...currentCounts,
      [oldStatus]: Math.max(0, currentCounts[oldStatus] - 1),
      cancelled: (currentCounts.cancelled || 0) + 1
    };

    this.activityStatusCountsByDraftKey.set(cacheKey, updatedCounts);
    this.persistModalSession();
  }

  onActivitySaved(activity: EditGroupActivity) {
    const draft = this.subgroupDrawerVisible ? this.subgroupDraft : this.editDraft;
    if (!draft || !activity.id) {
      return;
    }

    const isSubgroup = this.subgroupDrawerVisible;
    const status = isSubgroup ? this.subgroupActivityStatus : this.groupActivityStatus;
    const expanded = isSubgroup ? this.subgroupActivitiesExpanded : this.activitiesExpanded;

    if (expanded) {
      this.loadFullActivitiesList(draft, status, true);
      return;
    }

    this.loadActivityStatusPreview(draft, status, true);
  }

  private removeActivityFromLocalState(deletedActivity: EditGroupActivity) {
    this.activitiesByDraftKey.forEach((activities, draftKey) => {
      if (!activities.some(activity => activity.id === deletedActivity.id)) {
        return;
      }

      const updated = activities.filter(activity => activity.id !== deletedActivity.id);
      const currentTotal = this.activityTotalsByDraftKey.get(draftKey) ?? activities.length;
      const currentCounts = this.activityStatusCountsByDraftKey.get(draftKey) ??
        this.countActivitiesByStatus(activities);
      const status = this.toActivityStatusCountKey(deletedActivity.status);

      this.activitiesByDraftKey.set(draftKey, updated);
      this.activityTotalsByDraftKey.set(draftKey, Math.max(0, currentTotal - 1));
      this.activityStatusCountsByDraftKey.set(draftKey, {
        ...currentCounts,
        [status]: Math.max(0, currentCounts[status] - 1)
      });
      this.deleteActivityStatusKeys(this.fullActivitiesLoadedByDraftKey, draftKey);
    });

    this.persistModalSession();
  }

  private persistModalSession(url = this.location.path()) {
    if (!this.isAnyModalVisible()) {
      this.clearModalSession();
      return;
    }

    const state = {
      url,
      modalScrollPosition: this.modalScrollPosition,
      groupDrawerScrollPosition: this.groupDrawerScrollPosition,
      subgroupDrawerScrollPosition: this.subgroupDrawerScrollPosition,
      activityDrawerScrollPosition: 0,
      selectedGroup: this.selectedGroup,
      editDrawerVisible: this.editDrawerVisible,
      editDraft: this.editDraft,
      editOriginal: this.editOriginal,
      editingName: this.editingName,
      editingDescription: this.editingDescription,
      editingWebsiteUrl: this.editingWebsiteUrl,
      editingSport: this.editingSport,
      openedInEditMode: this.openedInEditMode,
      coverPhotoMenuVisible: this.coverPhotoMenuVisible,
      membersExpanded: this.membersExpanded,
      subgroupsExpanded: this.subgroupsExpanded,
      activitiesExpanded: this.activitiesExpanded,
      editMembers: this.getComputedMembers(this.editDraft),
      editMemberTotal: this.getMemberListTotal(this.editDraft),
      editSubgroups: this.getComputedSubgroups(this.editDraft),
      editSubgroupTotal: this.getSubgroupListTotal(this.editDraft),
      editActivities: this.getComputedActivities(this.editDraft),
      editActivityTotal: this.getActivityListTotal(this.editDraft),
      editActivityStatusCounts: this.getActivityStatusCounts(this.editDraft),
      subgroupDrawerVisible: this.subgroupDrawerVisible,
      subgroupDraft: this.subgroupDraft,
      subgroupOriginal: this.subgroupOriginal,
      subgroupEditingName: this.subgroupEditingName,
      subgroupEditingDescription: this.subgroupEditingDescription,
      subgroupEditingWebsiteUrl: this.subgroupEditingWebsiteUrl,
      subgroupEditingSport: this.subgroupEditingSport,
      subgroupCoverPhotoMenuVisible: this.subgroupCoverPhotoMenuVisible,
      subgroupMembersExpanded: this.subgroupMembersExpanded,
      subgroupSubgroupsExpanded: this.subgroupSubgroupsExpanded,
      subgroupActivitiesExpanded: this.subgroupActivitiesExpanded,
      subgroupMembers: this.getComputedMembers(this.subgroupDraft),
      subgroupMemberTotal: this.getMemberListTotal(this.subgroupDraft),
      subgroupSubgroups: this.getComputedSubgroups(this.subgroupDraft),
      subgroupSubgroupTotal: this.getSubgroupListTotal(this.subgroupDraft),
      subgroupActivities: this.getComputedActivities(this.subgroupDraft),
      subgroupActivityTotal: this.getActivityListTotal(this.subgroupDraft),
      subgroupActivityStatusCounts: this.getActivityStatusCounts(this.subgroupDraft),
      deleteDialogVisible: this.deleteDialogVisible,
      deleteConfirmed: this.deleteConfirmed,
      groupToDelete: this.groupToDelete,
      deleteTargetType: this.deleteTargetType,
      deleteTargetName: this.deleteTargetName,
      activityDrawerVisible: false,
      activityDraft: null,
      activityOriginal: null,
      activityLevel: 'all',
      activityOriginalLevel: 'all',
      activityEditingName: false,
      activityEditingDescription: false,
      activityEditingSport: false,
      activityEditingLevel: false,
      activityCoverPhotoMenuVisible: false,
      activityParticipantsExpanded: false
    } satisfies GroupsModalSessionState;
    this.patchActivityFields(state);

    this.modalSession.save(state);
  }

  onModalDraftChanged() {
    queueMicrotask(() => this.persistModalSession());
  }

  onDeleteConfirmationChange(confirmed: boolean) {
    this.deleteConfirmed = confirmed;
    this.persistModalSession();
  }

  onDrawerBodyScroll(target: ModalEditTarget, event: Event) {
    const scrollPosition = (event.target as HTMLElement).scrollTop;
    if (target === 'group') {
      this.groupDrawerScrollPosition = scrollPosition;
    } else if (target === 'subgroup') {
      this.subgroupDrawerScrollPosition = scrollPosition;
    }

    this.persistModalSession();
  }

  onDrawerShown(target: ModalEditTarget) {
    this.restoreDrawerScrollPosition(target, target === this.getTopVisibleDrawerTarget());
  }

  private clearModalSession() {
    this.modalSession.clear();
  }

  private readModalSession(): GroupsModalSessionState | null {
    return this.modalSession.read();
  }

  private applyStoredModalSession(state: GroupsModalSessionState) {
    this.subgroupDrawerStack = [];
    const needsDrawerScrollRestore =
      (state.groupDrawerScrollPosition ?? 0) > 0 ||
      (state.subgroupDrawerScrollPosition ?? 0) > 0 ||
      (state.activityDrawerScrollPosition ?? 0) > 0;

    if (needsDrawerScrollRestore) {
      this.beginScrollRestoration();
    }

    this.selectedGroup = state.selectedGroup;
    this.editDrawerVisible = !!state.editDrawerVisible && !!state.editDraft;
    this.skipEditDrawerEnterAnimation = this.editDrawerVisible;
    this.editDraft = state.editDraft;
    this.editOriginal = state.editOriginal;
    this.editingName = state.editingName;
    this.editingDescription = state.editingDescription;
    this.editingWebsiteUrl = !!state.editingWebsiteUrl;
    this.editingSport = !!state.editingSport;
    this.openedInEditMode = state.openedInEditMode;
    this.coverPhotoMenuVisible = state.coverPhotoMenuVisible;
    this.membersExpanded = state.membersExpanded;
    this.subgroupsExpanded = state.subgroupsExpanded;
    this.activitiesExpanded = state.activitiesExpanded;
    this.subgroupDrawerVisible = !!state.subgroupDrawerVisible && !!state.subgroupDraft;
    this.skipSubgroupDrawerEnterAnimation = this.subgroupDrawerVisible;
    this.subgroupDraft = state.subgroupDraft;
    this.subgroupOriginal = state.subgroupOriginal;
    this.subgroupEditingName = state.subgroupEditingName;
    this.subgroupEditingDescription = state.subgroupEditingDescription;
    this.subgroupEditingWebsiteUrl = !!state.subgroupEditingWebsiteUrl;
    this.subgroupEditingSport = !!state.subgroupEditingSport;
    this.subgroupCoverPhotoMenuVisible = state.subgroupCoverPhotoMenuVisible;
    this.subgroupMembersExpanded = state.subgroupMembersExpanded;
    this.subgroupSubgroupsExpanded = state.subgroupSubgroupsExpanded;
    this.subgroupActivitiesExpanded = state.subgroupActivitiesExpanded;
    this.restoreReadonlyListsFromSession(state);
    this.activityDrawerHost?.applySessionState(this.toActivityDrawerSession(state));
    this.groupDrawerScrollPosition = state.groupDrawerScrollPosition ?? 0;
    this.subgroupDrawerScrollPosition = state.subgroupDrawerScrollPosition ?? 0;
    this.deleteDialogVisible = state.deleteDialogVisible;
    this.skipDeleteDialogEnterAnimation = this.deleteDialogVisible;
    this.deleteDialogClosing = false;
    this.deleteConfirmed = state.deleteConfirmed;
    this.groupToDelete = state.groupToDelete;
    this.deleteTargetType = state.deleteTargetType;
    this.deleteTargetName = state.deleteTargetName;
    this.updateModalViewportLock(state.modalScrollPosition);
    this.restoreVisibleDrawerScrollPositions();
  }

  private restoreReadonlyListsFromSession(state: GroupsModalSessionState) {
    if (state.editDraft) {
      this.restoreReadonlyListCache(
        state.editDraft,
        state.editMembers,
        state.editMemberTotal,
        state.editSubgroups,
        state.editSubgroupTotal,
        state.editActivities,
        state.editActivityTotal,
        state.editActivityStatusCounts
      );
    }

    if (state.subgroupDraft) {
      this.restoreReadonlyListCache(
        state.subgroupDraft,
        state.subgroupMembers,
        state.subgroupMemberTotal,
        state.subgroupSubgroups,
        state.subgroupSubgroupTotal,
        state.subgroupActivities,
        state.subgroupActivityTotal,
        state.subgroupActivityStatusCounts
      );
    }
  }

  private restoreReadonlyListCache(
    draft: EditGroupDraft,
    members: EditGroupMember[] = [],
    memberTotal = members.length,
    subgroups: EditGroupSubgroup[] = [],
    subgroupTotal = subgroups.length,
    activities: EditGroupActivity[] = [],
    activityTotal = activities.length,
    activityStatusCounts?: ActivityStatusCounts
  ) {
    const cacheKey = this.getDraftCacheKey(draft);
    this.membersByDraftKey.set(cacheKey, members);
    this.memberTotalsByDraftKey.set(cacheKey, memberTotal);
    this.subgroupsByDraftKey.set(cacheKey, subgroups);
    this.subgroupTotalsByDraftKey.set(cacheKey, subgroupTotal);
    this.activitiesByDraftKey.set(cacheKey, activities);
    this.activityTotalsByDraftKey.set(cacheKey, activityTotal);
    this.activityStatusCountsByDraftKey.set(cacheKey, activityStatusCounts ?? this.countActivitiesByStatus(activities));
  }

  private loadReferenceData(showLoadingIndicator = true) {
    if (showLoadingIndicator) {
      this.summaryLoading = true;
    }
    this.operationSubscriptions.add(this.groupsApi.getSummary().subscribe({
      next: summary => {
        this.summary = summary;
        this.summaryLoading = false;
        this.cdr.markForCheck();
      },
      error: error => {
        this.summaryLoading = false;
        this.showOperationError(this.getApiErrorMessage(error, 'Unable to load group KPI cards. Please try again.'));
        this.cdr.markForCheck();
      }
    }));
    this.operationSubscriptions.add(this.groupsApi.getSportTypes().subscribe(sportTypes => {
      this.sportTypeConfigs = sportTypes;
      const sortedOptions = sportTypes.map(({ label, value }) => ({ label, value }));
      this.editGroupSportOptions = toTwoColumnOptions(sortedOptions);
      this.sportTypes = [{ label: 'All sport types', value: 'all' }, ...sortedOptions];
      if (this.selectedSportType !== 'all' && !sortedOptions.some(option => option.value === this.selectedSportType)) {
        this.selectedSportType = 'all';
        this.persistPageState();
      }
      this.cdr.markForCheck();
    }));
  }

  private applyStoredPageState() {
    const state = this.modalSession.readPageState();
    if (!state) {
      return;
    }

    this.showFilter = state.showFilter;
    this.searchQuery = state.searchQuery ?? '';
    this.selectedSportType = state.selectedSportType ?? 'all';
    this.tableFirst = state.tableFirst ?? 0;
    this.tableRows = state.tableRows ?? 10;
    this.tableSortField = state.tableSortField;
    this.tableSortOrder = state.tableSortOrder;
    this.pageScrollPosition = state.pageScrollPosition ?? 0;
    this.groups = state.groupsSnapshot ?? [];
    this.loadedGroupsSnapshot = state.groupsSnapshot ?? [];
    this.totalRecords = state.totalRecords ?? this.groups.length;

    if (this.isReloadNavigation) {
      this.pageScrollPosition = 0;
    }
  }

  private getInitialScrollRestorePosition(): number {
    const queryParamMap = this.route.snapshot.queryParamMap;
    const hasModeQuery =
      queryParamMap.has('group') ||
      queryParamMap.has('subgroup') ||
      queryParamMap.has('activity') ||
      queryParamMap.has('activities') ||
      queryParamMap.has('delete') ||
      queryParamMap.has('mode') ||
      queryParamMap.has('edit');

    if (hasModeQuery) {
      const storedState = this.readModalSession();
      if (storedState?.url === this.location.path() && this.hasVisibleModalInSession(storedState)) {
        if (this.isReloadNavigation) {
          const restoredState = { ...storedState, deleteDialogVisible: false };
          if (!this.hasVisibleModalInSession(restoredState)) {
            return 0;
          }
        }
        return storedState.modalScrollPosition ?? 0;
      }
    }

    return this.pageScrollPosition;
  }

  private prepareEarlyPageScrollLock() {
    const scrollY = this.getInitialScrollRestorePosition();
    this.pendingPageScrollRestore = scrollY;
    this.lockPageScrollVisual(scrollY);
  }

  private lockPageScrollVisual(scrollY: number) {
    this.viewportScroll.lockPageAt(scrollY);
    this.hasEarlyPageScrollLock = true;
  }

  private releasePendingPageScrollLock() {
    if (!this.hasEarlyPageScrollLock) {
      return;
    }
    this.viewportScroll.releasePageLock();
    this.hasEarlyPageScrollLock = false;
  }

  private finalizePageScrollRestore() {
    const scrollY = this.pendingPageScrollRestore > 0 ? this.pendingPageScrollRestore : this.pageScrollPosition;
    if (scrollY <= 0) {
      this.initialPageScrollRestored = true;
      return;
    }

    this.pendingPageScrollRestore = 0;
    this.document.defaultView?.scrollTo(0, scrollY);
    this.pageScrollPosition = scrollY;
    this.initialPageScrollRestored = true;
    this.startPostRestoreScrollGuard(scrollY);
  }

  private persistPageState() {
    this.modalSession.savePageState({
      showFilter: this.showFilter,
      searchQuery: this.searchQuery,
      selectedSportType: this.selectedSportType,
      tableFirst: this.tableFirst,
      tableRows: this.tableRows,
      tableSortField: this.tableSortField,
      tableSortOrder: this.tableSortOrder,
      pageScrollPosition: this.pageScrollPosition,
      groupsSnapshot: this.loadedGroupsSnapshot,
      totalRecords: this.totalRecords
    });
  }

  private restorePageScrollPosition() {
    if (this.initialPageScrollRestored || this.isAnyModalVisible()) {
      return;
    }

    const desiredScrollY = this.pendingPageScrollRestore > 0 ? this.pendingPageScrollRestore : this.pageScrollPosition;
    if (desiredScrollY <= 0) {
      this.initialPageScrollRestored = true;
      this.releasePendingPageScrollLock();
      this.document.defaultView?.scrollTo(0, 0);
      return;
    }

    this.restorePageScrollPositionWithRetry(desiredScrollY, 0);
  }

  private restorePageScrollPositionWithRetry(scrollY: number, attempt: number) {
    this.document.defaultView?.requestAnimationFrame(() => {
      if (this.isAnyModalVisible()) {
        return;
      }

      this.document.defaultView?.scrollTo(0, scrollY);
      const currentScroll = this.document.defaultView?.scrollY ?? 0;
      if (Math.abs(currentScroll - scrollY) <= 1 || attempt >= 12) {
        this.pendingPageScrollRestore = 0;
        this.pageScrollPosition = scrollY;
        this.initialPageScrollRestored = true;
        this.startPostRestoreScrollGuard(scrollY);
        return;
      }

      this.restorePageScrollPositionWithRetry(scrollY, attempt + 1);
    });
  }

  private startPostRestoreScrollGuard(scrollY: number) {
    if (scrollY <= 0) {
      this.releasePendingPageScrollLock();
      return;
    }

    this.stopPostRestoreScrollGuard();
    this.postRestoreTargetScrollY = scrollY;
    this.lockPageScrollVisual(scrollY);
    this.postRestoreScrollGuardLockActive = true;
    this.postRestoreScrollGuardUntil = Date.now() + 2500;

    const tick = () => {
      if (Date.now() > this.postRestoreScrollGuardUntil || this.isAnyModalVisible()) {
        this.stopPostRestoreScrollGuard();
        return;
      }

      const currentScrollY = this.document.defaultView?.scrollY ?? 0;
      if (currentScrollY <= 1) {
        this.document.defaultView?.scrollTo(0, scrollY);
      }

      this.postRestoreScrollGuardFrame = this.document.defaultView?.requestAnimationFrame(tick) ?? null;
    };

    this.postRestoreScrollGuardFrame = this.document.defaultView?.requestAnimationFrame(tick) ?? null;
  }

  private stopPostRestoreScrollGuard() {
    if (this.postRestoreScrollGuardFrame === null) {
      if (this.postRestoreScrollGuardLockActive) {
        this.releasePendingPageScrollLock();
        this.postRestoreScrollGuardLockActive = false;
      }
      return;
    }

    this.document.defaultView?.cancelAnimationFrame(this.postRestoreScrollGuardFrame);
    this.postRestoreScrollGuardFrame = null;
    this.postRestoreScrollGuardUntil = 0;
    this.postRestoreTargetScrollY = 0;
    if (this.postRestoreScrollGuardLockActive) {
      this.releasePendingPageScrollLock();
      this.postRestoreScrollGuardLockActive = false;
    }
  }

  private restoreVisibleDrawerScrollPositions() {
    const topDrawer = this.getTopVisibleDrawerTarget();
    if (this.editDrawerVisible && topDrawer !== 'group') {
      this.restoreDrawerScrollPosition('group');
    }
    if (this.subgroupDrawerVisible && topDrawer !== 'subgroup') {
      this.restoreDrawerScrollPosition('subgroup');
    }
    if (this.isActivityDrawerVisible() && topDrawer !== 'activity') {
      this.restoreDrawerScrollPosition('activity');
    }
    if (topDrawer) {
      this.restoreDrawerScrollPosition(topDrawer, true);
      return;
    }

    this.completeScrollRestoration();
  }

  private restoreDrawerScrollPosition(target: ModalEditTarget, revealAfterRestore = false, attempt = 0) {
    const scrollPosition = target === 'group'
      ? this.groupDrawerScrollPosition
      : target === 'subgroup'
        ? this.subgroupDrawerScrollPosition
        : this.activityDrawerHost?.scrollPosition ?? 0;

    this.document.defaultView?.requestAnimationFrame(() => {
      const body = this.document.querySelector<HTMLElement>(`[data-groups-drawer-body="${target}"]`);
      if (body) {
        body.scrollTop = scrollPosition;
        if (revealAfterRestore) {
          this.completeScrollRestoration();
        }
        return;
      }

      if (revealAfterRestore && attempt < 12) {
        this.restoreDrawerScrollPosition(target, revealAfterRestore, attempt + 1);
        return;
      }

      // Fallback: do not keep page hidden forever if drawer body never appears.
      if (revealAfterRestore) {
        this.completeScrollRestoration();
      }
    });
  }

  private getTopVisibleDrawerTarget(): ModalEditTarget | null {
    if (this.isActivityDrawerVisible()) {
      return 'activity';
    }
    if (this.subgroupDrawerVisible) {
      return 'subgroup';
    }
    if (this.editDrawerVisible) {
      return 'group';
    }
    return null;
  }
  private beginScrollRestoration() {
    this.scrollRestorationPending = true;
    this.document.documentElement.classList.add('groups-scroll-restoring');
  }

  private completeScrollRestoration() {
    if (!this.scrollRestorationPending) {
      return;
    }

    this.scrollRestorationPending = false;
    this.document.documentElement.classList.remove('groups-scroll-restoring');
  }

  onFilterChanged() {
    this.searchReload.clear();
    this.tableFirst = 0;
    this.persistPageState();
    this.loadGroups(false);
  }

  onSearchChanged() {
    this.tableFirst = 0;
    this.persistPageState();
    this.searchReload.schedule(() => {
      this.loadGroups(false);
    });
  }

  /** The three quick filters of the toolbar reload from page one. */
  onCityFilterChanged() {
    this.onSearchChanged();
  }

  toggleWithoutAdmins() {
    this.withoutAdminsOnly = !this.withoutAdminsOnly;
    this.tableFirst = 0;
    this.loadGroups(false);
  }

  togglePrivateOnly() {
    this.privateOnly = !this.privateOnly;
    this.tableFirst = 0;
    this.loadGroups(false);
  }

  onTableLazyLoad(event: TableLazyLoadEvent) {
    if (this.shouldIgnoreInitialLazyLoad) {
      this.shouldIgnoreInitialLazyLoad = false;
      return;
    }

    const tableState = getManagementTableLazyState(event, this.tableRows, this.isGroupsListSortField);
    const pageEventKey = this.getTablePageEventKey(tableState.first, tableState.rows);
    const shouldSkipDuplicatePageLoad = !!this.lastPageEventKey && this.lastPageEventKey === pageEventKey;
    this.lastPageEventKey = '';
    this.tableFirst = tableState.first;
    this.tableRows = tableState.rows;
    const sortState = this.getNextSortState(tableState.sortField, tableState.sortOrder);
    this.tableSortField = sortState.sortField;
    this.tableSortOrder = sortState.sortOrder;
    if (sortState.cleared) {
      this.clearTableSort(this.groupsTable);
    }
    this.persistPageState();
    if (shouldSkipDuplicatePageLoad) {
      return;
    }

    this.loadGroups();
  }

  onTablePage(event: TablePageEvent) {
    this.tableFirst = event.first;
    this.tableRows = event.rows;
    this.lastPageEventKey = this.getTablePageEventKey(event.first, event.rows);
    this.persistPageState();
    this.loadGroups();
  }

  private getTablePageEventKey(first: number, rows: number): string {
    return `${first}:${rows}`;
  }

  private getNextSortState(
    sortField?: GroupsListSortField,
    sortOrder?: 'asc' | 'desc'
  ): { sortField?: GroupsListSortField; sortOrder?: 'asc' | 'desc'; cleared?: boolean } {
    const shouldClearSort =
      sortField === this.tableSortField &&
      this.tableSortOrder === 'desc' &&
      sortOrder === 'asc';

    if (shouldClearSort) {
      return { cleared: true };
    }

    return {
      sortField,
      sortOrder
    };
  }

  private clearTableSort(table?: Table) {
    if (!table) {
      return;
    }

    table.sortField = null;
    table.sortOrder = 0;
    table.tableService.onSort(null);
  }

  private loadGroups(showLoadingIndicator = true) {
    this.groupsRequestSubscription?.unsubscribe();
    if (showLoadingIndicator) {
      this.tableLoading = true;
    }
    this.groupsRequestSubscription = this.groupsApi.list(this.getGroupsListQuery()).subscribe({
      next: response => {
        if (!response.data.length && response.total > 0 && this.tableFirst >= response.total) {
          this.tableFirst = getPreviousPageFirst(response.total, this.tableRows);
          this.loadGroups(showLoadingIndicator);
          return;
        }

        this.groups = response.data;
        this.loadedGroupsSnapshot = response.data;
        this.groupsListLoaded = true;
        this.totalRecords = response.total;
        this.tableRows = response.pageSize || this.tableRows;
        this.tableFirst = Math.max((response.page || 1) - 1, 0) * this.tableRows;
        this.tableLoading = false;
        this.persistPageState();
        this.tryRestoreModalStateFromRoute();
        this.cdr.markForCheck();
        this.restorePageScrollPosition();
      },
      error: error => {
        this.groups = [];
        this.totalRecords = 0;
        this.groupsListLoaded = false;
        this.tableLoading = false;
        this.showOperationError(this.getApiErrorMessage(error, 'Unable to load groups. Please try again.'));
        this.cdr.markForCheck();
        this.restorePageScrollPosition();
      }
    });
  }

  private getGroupsListQuery(): GroupsListQuery {
    return {
      search: this.searchQuery,
      sportType: this.selectedSportType,
      cityContains: this.cityFilter || undefined,
      withoutAdmins: this.withoutAdminsOnly || undefined,
      privacy: this.privateOnly ? 'private' : undefined,
      sortField: this.tableSortField,
      sortOrder: this.tableSortOrder,
      page: Math.floor(this.tableFirst / this.tableRows) + 1,
      pageSize: this.tableRows
    };
  }

  private isGroupsListSortField(field?: string | string[] | null): field is GroupsListSortField {
    return field === 'name' ||
      field === 'owner.displayName' ||
      field === 'sport' ||
      field === 'totalSubgroups' ||
      field === 'totalMembers' ||
      field === 'totalActivities' ||
      field === 'subgroups' ||
      field === 'members' ||
      field === 'activities';
  }

  private performMutation(request: Observable<unknown>, afterSuccess: () => void, reloadList = true) {
    this.operationSubscriptions.add(request.subscribe({
      next: () => {
        this.operationErrorMessage = '';
        if (reloadList) {
          this.loadGroups(false);
        }
        afterSuccess();
        this.cdr.markForCheck();
      },
      error: error => {
        this.showOperationError(this.getApiErrorMessage(error, 'Unable to save changes. Please try again.'));
        this.cdr.markForCheck();
      }
    }));
  }

  private hydrateRestoredModalDetails() {
    const groupId = this.editDraft?.id;
    if (groupId) {
      this.operationSubscriptions.add(this.groupsApi.getDetail(groupId).subscribe({
        next: detail => {
          if (this.editDraft?.id !== groupId) {
            return;
          }

          if (!this.isGroupEditActionMode()) {
            this.applyGroupDetail(groupId, detail);
            this.editOriginal = this.editDraft ? { ...this.editDraft } : null;
          } else {
            this.setReadonlyListsForDraft(this.editDraft, detail);
          }

          this.persistModalSession();
          this.cdr.markForCheck();
        },
        error: error => {
          if (this.editDraft?.id === groupId) {
            this.showOperationError(this.getApiErrorMessage(error, 'Unable to load group detail. Please try again.'));
            this.cdr.markForCheck();
          }
        }
      }));
    }

    const subgroupId = this.subgroupDraft?.id;
    if (subgroupId) {
      this.operationSubscriptions.add(this.groupsApi.getDetail(subgroupId).subscribe({
        next: detail => {
          if (this.subgroupDraft?.id !== subgroupId) {
            return;
          }

          if (!this.isSubgroupEditActionMode()) {
            this.subgroupDraft = this.applySavedCoverPhotoOverride(subgroupId, { ...detail.draft, id: subgroupId });
            this.subgroupOriginal = { ...this.subgroupDraft };
            this.patchSubgroupCardsFromDraft(this.subgroupDraft);
          }

          this.setReadonlyListsForDraft(this.subgroupDraft, detail);
          this.persistModalSession();
          this.cdr.markForCheck();
        },
        error: error => {
          if (this.subgroupDraft?.id === subgroupId) {
            this.showOperationError(this.getApiErrorMessage(error, 'Unable to load subgroup detail. Please try again.'));
            this.cdr.markForCheck();
          }
        }
      }));
    }
  }

  private hasVisibleModalInSession(state: GroupsModalSessionState): boolean {
    return state.editDrawerVisible || state.subgroupDrawerVisible || !!state.activityDrawerVisible || state.deleteDialogVisible;
  }

  private restoreModalStateFromUrl(): boolean {
    const params = this.route.snapshot.queryParamMap;
    const groupId = params.get('group');
    const originalDeleteType = params.get('delete') as 'group' | 'subgroup' | 'activity' | null;
    let deleteType = originalDeleteType;

    if (this.isReloadNavigation && deleteType) {
      deleteType = null;
    }
    const group = groupId
      ? this.loadedGroupsSnapshot.find(item => item.id === groupId)
      : null;

    if (!group) {
      if (groupId) {
        this.restoreGroupFromRoute(groupId, params);
        return true;
      }
      return false;
    }

    const shouldOpenGroupDrawer =
      !originalDeleteType ||
      params.has('subgroup') ||
      params.has('activity') ||
      params.has('activities') ||
      params.has('mode');

    if (shouldOpenGroupDrawer) {
      this.selectedGroup = group;
      const editDraft = { ...this.buildEditDraft(group), id: group.id };
      this.editDraft = editDraft;
      this.editOriginal = { ...editDraft };
      this.editDrawerVisible = true;
      this.skipEditDrawerEnterAnimation = true;
    }

    const subgroupId = params.get('subgroup');
    const activityId = params.get('activity') ?? params.get('activities');
    const editTarget = params.get('mode') === 'edit'
      ? params.get('edit') as ModalEditTarget | null
      : null;
    if (subgroupId && this.editDraft) {
      const subgroup = this.getComputedSubgroups(this.editDraft)
        .find(item => item.id === subgroupId);
      if (subgroup) {
        this.setSubgroupDraft(subgroup);
        this.subgroupDrawerVisible = true;
        this.skipSubgroupDrawerEnterAnimation = true;
      } else {
        this.restoreSubgroupFromRoute(subgroupId, activityId, editTarget);
      }
    }

    if (activityId && this.editDraft && (!subgroupId || this.subgroupDraft?.id === subgroupId)) {
      const activityParent = this.subgroupDraft ?? this.editDraft;
      const activity = this.getComputedActivities(activityParent)
        .find(item => item.id === activityId);
      this.activityDrawerHost?.open(
        activity ?? this.createActivityRouteShell(activityId),
        { skipEnterAnimation: true }
      );
    }

    if (editTarget && (!subgroupId || this.subgroupDraft?.id === subgroupId)) {
      this.restoreEditMode(editTarget);
    }

    if (deleteType) {
      const deleteTarget = this.getRestoredDeleteTarget(deleteType, group);
      if (!deleteTarget) {
        return false;
      }

      this.groupToDelete = deleteTarget;
      this.deleteTargetType = deleteType;
      this.deleteTargetName = deleteTarget.name;
      this.deleteDialogVisible = true;
      this.skipDeleteDialogEnterAnimation = true;
      this.deleteDialogClosing = false;
      this.deleteConfirmed = false;
    }

    if (!this.isAnyModalVisible()) {
      return false;
    }

    this.updateModalViewportLock();
    this.syncModeUrl();
    return true;
  }

  private restoreGroupFromRoute(groupId: string, params: ParamMap) {
    this.operationSubscriptions.add(this.groupsApi.getDetail(groupId).subscribe({
      next: detail => {
        const draft = { ...detail.draft, id: groupId };
        this.selectedGroup = {
          id: groupId,
          name: draft.name,
          admin: { name: '-', email: '' },
          sport: draft.sport,
          subgroups: detail.subgroupsTotal,
          members: detail.membersTotal,
          activities: detail.activitiesTotal,
          totalSubgroups: detail.subgroupsTotal,
          totalMembers: detail.membersTotal,
          totalActivities: detail.activitiesTotal,
          createdOn: draft.createdOn
        };
        this.editDraft = draft;
        this.editOriginal = { ...draft };
        this.editDrawerVisible = true;
        this.skipEditDrawerEnterAnimation = true;
        this.setReadonlyListsForDraft(draft, detail);

        const subgroupId = params.get('subgroup');
        const activityId = params.get('activity') ?? params.get('activities');
        const editTarget = params.get('mode') === 'edit'
          ? params.get('edit') as ModalEditTarget | null
          : null;

        if (subgroupId) {
          this.restoreSubgroupFromRoute(subgroupId, activityId, editTarget);
        } else {
          if (activityId) {
            const activity = detail.activities.find(item => item.id === activityId) ??
              this.createActivityRouteShell(activityId);
            this.activityDrawerHost?.open(activity, { skipEnterAnimation: true });
          }
          if (editTarget) {
            this.restoreEditMode(editTarget);
          }
          this.updateModalViewportLock();
          this.syncModeUrl();
        }

        this.cdr.markForCheck();
      },
      error: error => {
        this.resetFailedModalRestore();
        this.clearModalSession();
        this.location.replaceState('/groups');
        this.showOperationError(this.getApiErrorMessage(
          error,
          'Unable to load group detail. Please try again.'
        ));
        this.cdr.markForCheck();
      }
    }));
  }

  private restoreSubgroupFromRoute(
    subgroupId: string,
    activityId: string | null,
    editTarget: ModalEditTarget | null
  ) {
    this.operationSubscriptions.add(this.groupsApi.getDetail(subgroupId).subscribe({
      next: detail => {
        const draft = this.applySavedCoverPhotoOverride(
          subgroupId,
          { ...detail.draft, id: subgroupId }
        );
        this.subgroupDraft = draft;
        this.subgroupOriginal = { ...draft };
        this.subgroupDrawerVisible = true;
        this.skipSubgroupDrawerEnterAnimation = true;
        this.setReadonlyListsForDraft(draft, detail);

        if (activityId) {
          const activity = detail.activities.find(item => item.id === activityId) ??
            this.createActivityRouteShell(activityId);
          this.activityDrawerHost?.open(activity, { skipEnterAnimation: true });
        }

        if (editTarget) {
          this.restoreEditMode(editTarget);
        }

        this.updateModalViewportLock();
        this.syncModeUrl();
        this.cdr.markForCheck();
      },
      error: error => {
        this.showOperationError(this.getApiErrorMessage(
          error,
          'Unable to load subgroup detail. Please try again.'
        ));
        this.cdr.markForCheck();
      }
    }));
  }

  private createActivityRouteShell(activityId: string): EditGroupActivity {
    return {
      id: activityId,
      name: '',
      time: '',
      date: '',
      thumbnailUrl: '',
      sport: '',
      location: '',
      host: '',
      participants: 0,
      status: 'incoming'
    };
  }

  private restoreEditMode(target: ModalEditTarget | null) {
    const editTarget = target ??
      (this.isActivityDrawerVisible() ? 'activity' : this.subgroupDrawerVisible ? 'subgroup' : 'group');

    if (editTarget === 'activity' && this.activityDrawerHost?.activityDraft) {
      this.activityDrawerHost.applySessionState({
        ...this.activityDrawerHost.getSessionState(),
        editingName: true,
        editingDescription: true,
        editingSport: true,
        editingLevel: true
      });
      return;
    }

    if (editTarget === 'subgroup' && this.subgroupDraft) {
      this.subgroupEditingName = true;
      this.subgroupEditingDescription = true;
      this.subgroupEditingSport = true;
      return;
    }

    if (this.editDraft) {
      this.openedInEditMode = true;
      this.editingName = true;
      this.editingDescription = true;
      this.editingWebsiteUrl = true;
      this.editingSport = true;
    }
  }

  private getRestoredDeleteTarget(
    type: 'group' | 'subgroup' | 'activity',
    group: GroupRow
  ): ManagementDeleteTarget | null {
    if (type === 'activity') {
      const deleteRequest = this.activityDrawerHost?.createDeleteRequest();
      return deleteRequest?.deleteTarget ?? null;
    }

    if (type === 'subgroup') {
      return this.subgroupDraft ? this.createDeleteTarget(this.subgroupDraft) : null;
    }

    return group;
  }

  private resetFailedModalRestore() {
    this.editDrawerVisible = false;
    this.skipEditDrawerEnterAnimation = false;
    this.editDraft = null;
    this.editOriginal = null;
    this.editingSport = false;
    this.editingWebsiteUrl = false;
    this.selectedGroup = null;
    this.clearSubgroupDrawerState();
    this.activityDrawerHost?.clear();
    this.deleteDialogVisible = false;
    this.skipDeleteDialogEnterAnimation = false;
    this.deleteDialogClosing = false;
    this.deleteConfirmed = false;
    this.groupToDelete = null;
    this.deleteTargetType = 'group';
    this.deleteTargetName = '';
    this.unlockModalViewport();
  }

  private clearToastTimeout() {
    this.toastDismiss.clear();
  }

  private scheduleToastDismiss(dismiss: () => void) {
    this.toastDismiss.schedule(dismiss, () => this.cdr.markForCheck());
  }

  private showDeleteSuccess(target: 'Group' | 'Subgroup', name: string) {
    this.deleteSuccessMessage = `<b>"${name}"</b> and its nested subgroups have been moved to <b>Deleted items</b>`;
    this.scheduleToastDismiss(() => {
      this.deleteSuccessMessage = '';
    });
  }

  private showOperationError(message: string) {
    this.operationErrorMessage = message;
    this.scheduleToastDismiss(() => {
      this.operationErrorMessage = '';
    });
  }

  private getApiErrorMessage(error: unknown, fallback: string): string {
    const apiError = this.normalizeApiError(error);
    const rawMessage = apiError.messages.find(message => message.trim());
    if (rawMessage) {
      const friendlyMessage = this.getFriendlyApiMessage(rawMessage);
      if (friendlyMessage) {
        return friendlyMessage;
      }
    }

    const statusMessage = this.getApiStatusMessage(apiError.status, fallback);
    if (statusMessage) {
      return statusMessage;
    }

    return fallback;
  }

  private normalizeApiError(error: unknown): { status?: number; messages: string[] } {
    const messages: string[] = [];
    const visit = (value: unknown, depth = 0): number | undefined => {
      if (!value || depth > 4) {
        return undefined;
      }

      if (typeof value === 'string') {
        messages.push(value);
        return undefined;
      }

      if (Array.isArray(value)) {
        value.forEach(item => visit(item, depth + 1));
        return undefined;
      }

      if (typeof value !== 'object') {
        return undefined;
      }

      const maybeError = value as {
        status?: unknown;
        statusCode?: unknown;
        error?: unknown;
        message?: unknown;
        detail?: unknown;
        details?: unknown;
        title?: unknown;
      };
      const status = typeof maybeError.status === 'number'
        ? maybeError.status
        : typeof maybeError.statusCode === 'number'
          ? maybeError.statusCode
          : undefined;

      [maybeError.message, maybeError.detail, maybeError.details, maybeError.title].forEach(item => visit(item, depth + 1));
      if (maybeError.error && maybeError.error !== value) {
        const nestedStatus = visit(maybeError.error, depth + 1);
        return status ?? nestedStatus;
      }

      return status;
    };

    const status = visit(error);
    return { status, messages };
  }

  private getApiStatusMessage(status: number | undefined, fallback: string): string | null {
    switch (status) {
      case 0:
        return 'Cannot connect to the server right now. Please check your connection and try again.';
      case 400:
      case 422:
        return 'Some information is invalid. Please review the fields and try again.';
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'This group may have been deleted or is no longer available.';
      case 409:
        return 'This group was updated by someone else. Please refresh and try again.';
      case 413:
        return 'The selected photo is too large. Please choose a smaller image.';
      case 415:
        return 'This file type is not supported. Please choose a different image.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
      case 502:
      case 503:
      case 504:
        return 'The server is temporarily unavailable. Please try again later.';
      default:
        return fallback ? null : 'Something went wrong. Please try again.';
    }
  }

  private getFriendlyApiMessage(message: string): string | null {
    const normalized = message.trim().toLowerCase();
    if (!normalized || normalized === 'string' || normalized === '[object object]') {
      return null;
    }

    if (normalized.includes('unauthorized')) {
      return 'Your session has expired. Please sign in again.';
    }

    if (normalized.includes('forbidden') || normalized.includes('admin access required')) {
      return 'You do not have permission to perform this action.';
    }

    if (normalized.includes('group not found') || normalized.includes('group_not_found')) {
      return 'This group may have been deleted or is no longer available.';
    }

    if (normalized.includes('invalid sport') || normalized.includes('invalid_sport_id')) {
      return 'The selected sport is invalid or no longer available. Please choose another sport.';
    }

    if (normalized.includes('invalid payload') || normalized.includes('at least one editable field')) {
      return 'Please update at least one field before saving.';
    }

    if (normalized.includes('failed to load')) {
      return null;
    }

    if (normalized.includes('failed to edit') || normalized.includes('edit_admin_group_failed')) {
      return 'Your changes could not be saved. Please try again.';
    }

    if (normalized.includes('failed to delete') || normalized.includes('delete_admin_group_failed')) {
      return 'This group could not be deleted. Please try again.';
    }

    if (normalized.includes('network') || normalized.includes('timeout')) {
      return 'Cannot connect to the server right now. Please check your connection and try again.';
    }

    return null;
  }

  private createDeleteTarget(source: { id?: string; name: string }): ManagementDeleteTarget {
    return {
      id: source.id || this.toRouteSlug(source.name),
      name: source.name
    };
  }

  private hasDraftChanges(draft: EditGroupDraft | null, original: EditGroupDraft | null): boolean {
    return hasGroupDraftChanges(draft, original);
  }

  private canSaveDraftChanges(
    draft: EditGroupDraft | null,
    original: EditGroupDraft | null,
    options: { requireDescription?: boolean } = {}
  ): boolean {
    return canSaveGroupDraftChanges(draft, original, options);
  }

  private setDraftCoverPhoto(
    draft: EditGroupDraft | null,
    update: (draft: EditGroupDraft) => void,
    coverPhotoUrl: string | null,
    coverPhotoFile?: File | null
  ) {
    const nextDraft = updateDraftCoverPhoto(draft, coverPhotoUrl, coverPhotoFile);
    if (nextDraft) {
      update(nextDraft);
    }
  }

  private discardDraftChanges(
    original: EditGroupDraft | null,
    update: (draft: EditGroupDraft) => void,
    afterDiscard: () => void
  ) {
    const nextDraft = resetDraftFromOriginal(original);
    if (nextDraft) {
      update(nextDraft);
    }

    afterDiscard();
  }

  private restoreGroupDraftField(field: 'name' | 'description' | 'websiteUrl' | 'sport', closeEditor: () => void) {
    if (this.editDraft && this.editOriginal) {
      this.editDraft = restoreDraftField(this.editDraft, this.editOriginal, field);
      if (!this.openedInEditMode) {
        closeEditor();
      }
    }
    this.syncModeUrl();
  }

  private restoreSubgroupDraftField(field: 'name' | 'description' | 'websiteUrl' | 'sport', closeEditor: () => void) {
    if (this.subgroupDraft && this.subgroupOriginal) {
      this.subgroupDraft = restoreDraftField(this.subgroupDraft, this.subgroupOriginal, field);
      closeEditor();
    }
    this.syncModeUrl();
  }

  private clearSubgroupDrawerState() {
    this.subgroupDrawerStack = [];
    this.subgroupDrawerVisible = false;
    this.skipSubgroupDrawerEnterAnimation = false;
    this.subgroupDraft = null;
    this.subgroupOriginal = null;
    this.subgroupEditingName = false;
    this.subgroupEditingDescription = false;
    this.subgroupEditingWebsiteUrl = false;
    this.subgroupEditingSport = false;
    this.subgroupCoverPhotoMenuVisible = false;
    this.subgroupCoverPhotoFailed = false;
    this.subgroupSaveSuccessVisible = false;
    this.subgroupDiscardSuccessVisible = false;
    this.subgroupMembersExpanded = false;
    this.subgroupSubgroupsExpanded = false;
    this.subgroupActivitiesExpanded = false;
    this.subgroupDrawerScrollPosition = 0;
  }

  getSportIconPath(sport: string): string {
    return getSportIconPath(sport, this.sportTypeConfigs);
  }

  hasDescription(description: string | null | undefined): boolean {
    return !!description?.trim();
  }

  getDescriptionText(description: string | null | undefined): string {
    return description?.trim() || 'No description provided';
  }

  hasWebsiteUrl(websiteUrl: string | null | undefined): boolean {
    return !!websiteUrl?.trim();
  }

  getWebsiteUrlText(websiteUrl: string | null | undefined): string {
    return websiteUrl?.trim() || 'No website link provided';
  }

  isDraftWebsiteUrlInvalid(draft: EditGroupDraft | null): boolean {
    return !!draft && !isGroupDraftWebsiteUrlValid(draft.websiteUrl);
  }

  getDraftSportIconPath(draft: EditGroupDraft | null): string | null {
    const icon = draft?.sportIcon?.trim();
    return icon ? this.toSportIconSource(icon) : null;
  }

  hasDraftSport(draft: EditGroupDraft | null): boolean {
    return !!draft?.sport?.trim();
  }

  private toSportIconSource(icon: string): string {
    const trimmedIcon = icon.trim();
    if (
      trimmedIcon.startsWith('http://') ||
      trimmedIcon.startsWith('https://') ||
      trimmedIcon.startsWith('data:') ||
      trimmedIcon.startsWith('assets/') ||
      trimmedIcon.startsWith('/assets/')
    ) {
      return trimmedIcon;
    }

    if (trimmedIcon.includes('/')) {
      return trimmedIcon;
    }

    return trimmedIcon.includes('.')
      ? `assets/icons/sport/${trimmedIcon}`
      : `assets/icons/sport/${trimmedIcon}.svg`;
  }

  getGroupLeadInitial(group: GroupRow): string {
    return (group.owner?.displayName ?? group.admin.name).trim().charAt(0).toUpperCase() || '-';
  }

  getGroupLeadAvatarUrl(group: GroupRow): string | null {
    const avatarUrl = group.owner?.avatarUrl?.trim();
    return avatarUrl || null;
  }

  onGroupLeadAvatarError(group: GroupRow) {
    if (group.owner) {
      group.owner = { ...group.owner, avatarUrl: undefined };
      this.cdr.markForCheck();
    }
  }

  getPaginationFirst(): number {
    return this.totalRecords ? this.tableFirst + 1 : 0;
  }

  getPaginationLast(): number {
    return this.totalRecords ? Math.min(this.tableFirst + this.tableRows, this.totalRecords) : 0;
  }

  get paginationReportText(): string {
    return `Showing ${this.getPaginationFirst()} to ${this.getPaginationLast()} of ${this.totalRecords} results`;
  }

  getGroupSportName(group: GroupRow): string {
    return typeof group.sport === 'string'
      ? group.sport
      : group.sport?.name ?? '';
  }

  getGroupSportIconSrc(group: GroupRow): string | undefined {
    if (!group.sport || typeof group.sport === 'string') {
      return undefined;
    }

    return group.sport.iconSrc;
  }

  getSportOptionValue(sport: string): string {
    return getSportOptionValue(sport, this.sportTypeConfigs);
  }

  getSelectedSportOptionValue(sport: string | null | undefined): string | null {
    const trimmedSport = sport?.trim();
    return trimmedSport ? this.getSportOptionValue(trimmedSport) : null;
  }

  getSportLabelFromValue(value: string): string {
    return getSportLabelFromValue(value, this.sportTypeConfigs);
  }

  private getSportIconFromValue(value: string): string | undefined {
    return this.sportTypeConfigs.find(sport => sport.value === value)?.icon;
  }

  private updateDraftSportSelection(draft: EditGroupDraft | null, value: string): EditGroupDraft | null {
    if (!draft) {
      return null;
    }

    return {
      ...draft,
      sport: this.getSportLabelFromValue(value),
      sportIcon: this.getSportIconFromValue(value)
    };
  }

  clearFilter() {
    this.searchReload.clear();
    this.selectedSportType = 'all';
    this.searchQuery = '';
    this.tableFirst = 0;
    this.persistPageState();
    this.loadGroups();
  }

  openDeleteDialog(group: ManagementDeleteTarget, targetType: 'group' | 'subgroup' | 'activity' = 'group') {
    this.deleteTargetType = targetType;
    if (targetType === 'group' || targetType === 'subgroup') {
      const draft = targetType === 'group' ? this.editDraft : this.subgroupDraft;
      if (draft) {
        const activityStatusCounts = this.getActivityStatusCounts(draft);
        const incomingCount = activityStatusCounts?.incoming ?? 0;
        this.groupToDelete = group;
        this.openDeleteGroupWithIncomingDialog(group.name, incomingCount);
        return;
      } else {
        this.operationSubscriptions.add(this.groupsApi.getDetail(group.id).subscribe({
          next: (detail) => {
            const incomingCount = detail.activityStatusCounts?.incoming ?? 0;
            this.groupToDelete = group;
            this.openDeleteGroupWithIncomingDialog(group.name, incomingCount);
            this.cdr.markForCheck();
          },
          error: () => {
            const incomingCount = 0;
            this.groupToDelete = group;
            this.openDeleteGroupWithIncomingDialog(group.name, incomingCount);
            this.cdr.markForCheck();
          }
        }));
        return;
      }
    }

    this.showDeleteConfirmationDialog(group, targetType);
  }

  private showDeleteConfirmationDialog(group: ManagementDeleteTarget, targetType: 'group' | 'subgroup' | 'activity') {
    this.groupToDelete = group;
    this.deleteTargetType = targetType;
    this.deleteTargetName = group.name;
    this.deleteConfirmed = false;
    this.deleteDialogClosing = false;
    this.skipDeleteDialogEnterAnimation = false;
    this.deleteDialogVisible = true;
    this.updateModalViewportLock();
    this.syncModeUrl();
  }

  closeDeleteDialog() {
    this.skipDeleteDialogEnterAnimation = false;
    this.deleteDialogVisible = false;
    this.deleteDialogClosing = true;
  }

  onDeleteDialogHidden() {
    if (this.deleteDialogVisible) {
      return;
    }

    this.deleteDialogClosing = false;
    this.skipDeleteDialogEnterAnimation = false;
    this.deleteConfirmed = false;
    this.groupToDelete = null;
    this.deleteTargetType = 'group';
    this.deleteTargetName = '';
    this.updateModalViewportLock();
    this.syncModeUrl();
  }

  openDeleteGroupWithIncomingDialog(groupName: string, incomingCount: number) {
    this.deleteGroupWithIncomingGroupName = groupName;
    this.deleteGroupWithIncomingCount = incomingCount;
    this.deleteGroupWithIncomingDialogClosing = false;
    this.skipDeleteGroupWithIncomingDialogEnterAnimation = false;
    this.deleteGroupWithIncomingConfirmed = false;
    this.deleteGroupWithIncomingDialogVisible = true;
    this.updateModalViewportLock();
  }

  closeDeleteGroupWithIncomingDialog() {
    this.skipDeleteGroupWithIncomingDialogEnterAnimation = false;
    this.deleteGroupWithIncomingDialogVisible = false;
    this.deleteGroupWithIncomingDialogClosing = true;
  }

  onDeleteGroupWithIncomingDialogHidden() {
    if (this.deleteGroupWithIncomingDialogVisible) {
      return;
    }

    this.deleteGroupWithIncomingDialogClosing = false;
    this.skipDeleteGroupWithIncomingDialogEnterAnimation = false;
    this.deleteGroupWithIncomingGroupName = '';
    this.deleteGroupWithIncomingCount = 0;
    this.updateModalViewportLock();
  }

  onDeleteGroupWithIncomingDialogConfirmed() {
    this.closeDeleteGroupWithIncomingDialog();
    // Proceed with delete after confirmation
    this.deleteTargetName = this.groupToDelete?.name ?? '';
    this.deleteConfirmed = true;
    this.confirmDelete();
  }

  confirmDelete() {
    if (this.deleteTargetType === 'activity') {
      this.deleteActivity();
      return;
    }

    if (this.deleteTargetType === 'subgroup') {
      this.deleteSubgroup();
      return;
    }

    this.deleteGroup();
  }

  private deleteGroup() {
    if (!this.groupToDelete || !this.deleteConfirmed) {
      return;
    }

    const deletedGroupName = this.groupToDelete.name;
    const deletedGroupId = this.groupToDelete.id;
    const shouldCloseGroupDrawer = this.editDrawerVisible && this.selectedGroup?.id === deletedGroupId;
    this.operationSubscriptions.add(this.groupsApi.deleteGroup(deletedGroupId).subscribe({
      next: () => {
        this.operationErrorMessage = '';
        if (shouldCloseGroupDrawer) {
          this.closeEditDrawer();
        }
        this.showDeleteSuccess('Group', deletedGroupName);
        this.loadReferenceData();
        this.loadGroups();
        this.cdr.markForCheck();
      },
      error: error => {
        this.showOperationError(this.getApiErrorMessage(error, 'Unable to delete group. Please try again.'));
        this.cdr.markForCheck();
      }
    }));
  }

  private deleteSubgroup() {
    if (!this.subgroupDraft || !this.deleteConfirmed) {
      return;
    }

    const deletedSubgroupName = this.subgroupDraft.name;
    const deletedSubgroupId = this.subgroupDraft.id || this.subgroupDraft.name;
    this.operationSubscriptions.add(this.groupsApi.deleteGroup(deletedSubgroupId).subscribe({
      next: () => {
        this.operationErrorMessage = '';
        this.closeSubgroupDrawer();
        this.showDeleteSuccess('Subgroup', deletedSubgroupName);
        this.removeSubgroupFromLocalState(deletedSubgroupId);
        if (this.editDraft) {
          this.loadFullSubgroupsList(this.editDraft, true);
        }
        this.loadReferenceData();
        this.loadGroups();
        this.cdr.markForCheck();
      },
      error: error => {
        this.showOperationError(this.getApiErrorMessage(error, 'Unable to delete subgroup. Please try again.'));
        this.cdr.markForCheck();
      }
    }));
  }

  private removeSubgroupFromLocalState(subgroupId: string) {
    const affectedParentIds = new Set<string>();

    this.subgroupsByDraftKey.forEach((subgroups, cacheKey) => {
      const nextSubgroups = subgroups.filter(subgroup => subgroup.id !== subgroupId);
      if (nextSubgroups.length === subgroups.length) {
        return;
      }

      affectedParentIds.add(cacheKey);
      this.subgroupsByDraftKey.set(cacheKey, nextSubgroups);
      const currentTotal = this.subgroupTotalsByDraftKey.get(cacheKey) ?? subgroups.length;
      this.subgroupTotalsByDraftKey.set(cacheKey, Math.max(0, currentTotal - 1));
      if (nextSubgroups.length < currentTotal - 1) {
        this.fullSubgroupsLoadedByDraftKey.delete(cacheKey);
      }
    });

    this.patchParentSubgroupCounts(affectedParentIds);
    this.groups = this.patchGroupRowsAfterSubgroupDelete(this.groups, affectedParentIds);
    this.loadedGroupsSnapshot = this.patchGroupRowsAfterSubgroupDelete(this.loadedGroupsSnapshot, affectedParentIds);

    this.persistModalSession();
  }

  private patchGroupRowsAfterSubgroupDelete(groups: GroupRow[], affectedParentIds: Set<string>): GroupRow[] {
    return groups.map(group => {
      if (!affectedParentIds.has(group.id)) {
        return group;
      }

      return {
        ...group,
        totalSubgroups: Math.max(0, (group.totalSubgroups ?? group.subgroups ?? 0) - 1),
        subgroups: Math.max(0, (group.subgroups ?? group.totalSubgroups ?? 0) - 1)
      };
    });
  }

  private patchParentSubgroupCounts(affectedParentIds: Set<string>) {
    if (!affectedParentIds.size) {
      return;
    }

    this.subgroupsByDraftKey.forEach((subgroups, cacheKey) => {
      let didUpdate = false;
      const nextSubgroups = subgroups.map(subgroup => {
        if (!affectedParentIds.has(subgroup.id)) {
          return subgroup;
        }

        didUpdate = true;
        return {
          ...subgroup,
          activities: Math.max(0, subgroup.activities - 1)
        };
      });

      if (didUpdate) {
        this.subgroupsByDraftKey.set(cacheKey, nextSubgroups);
      }
    });
  }

  sheetGroupId: string | null = null;
  sheetGroupRow: GroupRow | null = null;
  sheetSidebarVisible = false;

  openSheet(group: GroupRow) {
    this.sheetGroupId = group.id;
    this.sheetGroupRow = group;
    this.sheetSidebarVisible = true;
  }

  onSheetEdit() {
    if (this.sheetGroupRow) {
      this.sheetSidebarVisible = false;
      this.openEditDrawer(this.sheetGroupRow, true);
    }
  }

  onSheetEngage() {
    if (this.sheetGroupRow) {
      this.sheetSidebarVisible = false;
      this.openEngage(this.sheetGroupRow);
    }
  }

  onSheetDelete() {
    if (this.sheetGroupRow) {
      this.sheetSidebarVisible = false;
      this.openDeleteDialog(this.sheetGroupRow);
    }
  }

  engageGroupId: string | null = null;
  engageGroupName = '';
  engageSportName = '';
  engageSidebarVisible = false;

  openEngage(group: GroupRow) {
    this.engageGroupId = group.id;
    this.engageGroupName = group.name;
    this.engageSportName = group.sportName || (typeof group.sport === 'string' ? group.sport : group.sport?.name) || '';
    this.engageSidebarVisible = true;
  }

  openEditDrawer(group: GroupRow, forceEditMode = false, shouldSyncUrl = true) {
    this.selectedGroup = group;
    this.groupDrawerScrollPosition = 0;
    this.subgroupDrawerScrollPosition = 0;
    this.subgroupDrawerStack = [];
    this.activityDrawerHost?.clear();
    const draft = this.buildEditDraft(group);
    this.editDraft = { ...draft };
    this.editOriginal = { ...draft };
    this.coverPhotoMenuVisible = false;
    this.coverPhotoFailed = false;
    this.membersExpanded = false;
    this.subgroupsExpanded = false;
    this.activitiesExpanded = false;
    this.saveSuccessVisible = false;
    this.discardSuccessVisible = false;
    this.openedInEditMode = forceEditMode;
    this.editingName = forceEditMode;
    this.editingDescription = forceEditMode;
    this.editingWebsiteUrl = forceEditMode;
    this.editingSport = forceEditMode;
    this.clearGroupDrawerCloseFallback();
    this.groupDrawerClosing = false;
    this.skipEditDrawerEnterAnimation = false;
    this.editDrawerVisible = true;
    this.updateModalViewportLock();
    if (shouldSyncUrl) {
      this.syncModeUrl();
    }
  }

  preventRowTextActivation(event: PointerEvent) {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, textarea, select, [role="button"]')) {
      return;
    }

    event.preventDefault();
  }

  closeEditDrawer() {
    if (!this.editDrawerVisible && !this.groupDrawerClosing) {
      this.finalizeStaleGroupDrawerDom();
      return;
    }

    this.clearGroupDrawerCloseFallback();
    this.groupDrawerClosing = true;
    this.skipEditDrawerEnterAnimation = false;
    this.editDrawerVisible = false;
    this.updateModalViewportLock();
    this.syncModeUrl();
    this.clearToastTimeout();
    this.scheduleGroupDrawerCloseFallback();
    this.cdr.markForCheck();
    if (this.drawerOnly) {
      this.scheduleIdleModalCleanup();
    }
  }

  onEditDrawerHidden() {
    if (this.editDrawerVisible) {
      return;
    }

    this.clearGroupDrawerCloseFallback();
    this.groupDrawerClosing = false;
    this.updateModalViewportLock();
    this.editDraft = null;
    this.editOriginal = null;
    this.coverPhotoMenuVisible = false;
    this.editingSport = false;
    this.editingWebsiteUrl = false;
    this.saveSuccessVisible = false;
    this.discardSuccessVisible = false;
    this.selectedGroup = null;
    this.skipEditDrawerEnterAnimation = false;
    this.clearSubgroupDrawerState();
    this.activityDrawerHost?.clear();
    if (!this.drawerOnly) {
      this.syncModeUrl();
    }
    this.scheduleIdleModalCleanup();
  }

  closeNestedDrawers() {
    this.activityDrawerHost?.close();
    this.subgroupDrawerStack = [];
    this.clearGroupDrawerCloseFallback();
    this.clearSubgroupDrawerCloseFallback();
    this.groupDrawerClosing = false;
    this.subgroupDrawerClosing = false;

    this.skipSubgroupDrawerEnterAnimation = false;
    this.skipEditDrawerEnterAnimation = false;
    this.subgroupDrawerVisible = false;
    this.editDrawerVisible = false;
    this.updateModalViewportLock();
    this.syncModeUrl();
    this.clearToastTimeout();
    this.scheduleIdleModalCleanup();
  }

  dismissSaveSuccess() {
    this.saveSuccessVisible = false;
    this.clearToastTimeout();
  }

  dismissDeleteSuccess() {
    this.deleteSuccessMessage = '';
    this.clearToastTimeout();
  }

  dismissOperationError() {
    this.operationErrorMessage = '';
    this.clearToastTimeout();
  }

  hasEditChanges(): boolean {
    return this.hasDraftChanges(this.editDraft, this.editOriginal);
  }

  canSaveEditChanges(): boolean {
    return this.canSaveDraftChanges(this.editDraft, this.editOriginal, {
      requireDescription: this.editingDescription,
    });
  }

  hasSubgroupChanges(): boolean {
    return this.hasDraftChanges(this.subgroupDraft, this.subgroupOriginal);
  }

  isGroupEditActionMode(): boolean {
    return this.editingName || this.editingDescription || this.editingWebsiteUrl || this.editingSport || this.coverPhotoMenuVisible || this.hasEditChanges();
  }

  getCoverPhotoSrc(): string {
    return this.editDraft?.coverPhotoUrl || this.defaultCoverPhotoUrl;
  }

  private isRemovablePhotoUrl(photoUrl: string | null | undefined): boolean {
    return !!photoUrl && photoUrl !== this.defaultCoverPhotoUrl;
  }

  hasCoverPhoto(): boolean {
    return !this.coverPhotoFailed && this.isRemovablePhotoUrl(this.editDraft?.coverPhotoUrl);
  }

  getSubgroupCoverPhotoSrc(): string {
    return this.subgroupDraft?.coverPhotoUrl || this.defaultCoverPhotoUrl;
  }

  hasSubgroupCoverPhoto(): boolean {
    return !this.subgroupCoverPhotoFailed && this.isRemovablePhotoUrl(this.subgroupDraft?.coverPhotoUrl);
  }

  onCoverPhotoError(event: Event, target: 'group' | 'subgroup') {
    const image = event.target as HTMLImageElement;
    if (image.src.includes(this.defaultCoverPhotoUrl)) {
      return;
    }

    image.src = this.defaultCoverPhotoUrl;
    if (target === 'group') {
      this.coverPhotoFailed = true;
    } else if (target === 'subgroup') {
      this.subgroupCoverPhotoFailed = true;
    }
    this.cdr.markForCheck();
  }

  onListThumbError(event: Event) {
    const image = event.target as HTMLImageElement;
    if (!image.src.includes(this.defaultCoverPhotoUrl)) {
      image.src = this.defaultCoverPhotoUrl;
      return;
    }

    image.style.display = 'none';
  }



  buildEditDraft(group: GroupRow): EditGroupDraft {
    const fallbackDraft = this.createGroupDraftShell(group);
    this.initializeReadonlyListsForDraft(fallbackDraft);
    this.operationSubscriptions.add(this.groupsApi.getDetail(group.id).subscribe({
      next: detail => {
      if (this.selectedGroup?.id !== group.id || !this.editDraft) {
        return;
      }

      this.applyGroupDetail(group.id, detail);
      this.editOriginal = { ...this.editDraft };
      this.persistModalSession();
      this.cdr.markForCheck();
      },
      error: error => {
        if (this.selectedGroup?.id === group.id) {
          this.showOperationError(this.getApiErrorMessage(error, 'Unable to load group detail. Please try again.'));
          this.cdr.markForCheck();
        }
      }
    }));
    return fallbackDraft;
  }

  private createGroupDraftShell(group: GroupRow): EditGroupDraft {
    return {
      id: group.id,
      name: group.name,
      createdOn: group.createdOn ?? '',
      coverPhotoUrl: null,
      description: '',
      websiteUrl: '',
      sport: this.getGroupSportName(group),
      sportIcon: typeof group.sport === 'string' ? undefined : group.sport?.icon
    };
  }

  private createSubgroupDraftShell(subgroup: EditGroupSubgroup): EditGroupDraft {
    const draft = {
      id: subgroup.id,
      name: subgroup.name,
      createdOn: '',
      coverPhotoUrl: subgroup.thumbnailUrl || null,
      description: subgroup.description ?? '',
      websiteUrl: subgroup.websiteUrl ?? '',
      sport: subgroup.sport
    };

    return this.applySavedCoverPhotoOverride(subgroup.id, draft);
  }

  private refreshUpdatedGroupDetail(groupId: string, savedCoverPhotoUrl?: string | null) {
    this.operationSubscriptions.add(this.groupsApi.getDetail(groupId).subscribe({
      next: detail => {
        if (this.editDraft?.id !== groupId) {
          return;
        }

        this.applyGroupDetail(groupId, detail);
        if (savedCoverPhotoUrl === null && this.editDraft) {
          this.editDraft = { ...this.editDraft, coverPhotoUrl: null };
        }
        this.editOriginal = { ...this.editDraft };
        this.persistModalSession();
        this.cdr.markForCheck();
      }
    }));
  }

  private refreshUpdatedSubgroupDetail(groupId: string, savedCoverPhotoUrl?: string | null) {
    this.operationSubscriptions.add(this.groupsApi.getDetail(groupId).subscribe({
      next: detail => {
        if (this.subgroupDraft?.id !== groupId) {
          return;
        }

        this.subgroupDraft = this.applySavedCoverPhotoOverride(groupId, { ...detail.draft, id: groupId });
        if (savedCoverPhotoUrl === null) {
          this.subgroupDraft = { ...this.subgroupDraft, coverPhotoUrl: null };
        }
        this.subgroupOriginal = { ...this.subgroupDraft };
        this.patchSubgroupCardsFromDraft(this.subgroupDraft);
        this.setReadonlyListsForDraft(this.subgroupDraft, detail);
        this.persistModalSession();
        this.cdr.markForCheck();
      }
    }));
  }

  private applyGroupDetail(groupId: string, detail: GroupManagementDetail) {
    this.editDraft = this.applySavedCoverPhotoOverride(groupId, { ...detail.draft, id: groupId });
    this.setReadonlyListsForDraft(this.editDraft, detail);
  }

  private applySavedCoverPhotoOverride(groupId: string, draft: EditGroupDraft): EditGroupDraft {
    return this.savedCoverPhotoOverridesByGroupId.has(groupId)
      ? { ...draft, coverPhotoUrl: this.savedCoverPhotoOverridesByGroupId.get(groupId) ?? null }
      : draft;
  }

  private rememberSavedCoverPhoto(groupId: string, draft: EditGroupDraft) {
    if (draft.coverPhotoUrl === null) {
      this.savedCoverPhotoOverridesByGroupId.set(groupId, null);
      return;
    }

    this.savedCoverPhotoOverridesByGroupId.delete(groupId);
  }

  toggleCoverPhotoMenu(event: Event) {
    event.stopPropagation();
    this.coverPhotoMenuVisible = !this.coverPhotoMenuVisible;
    this.syncModeUrl();
  }

  closeCoverPhotoMenu() {
    if (!this.coverPhotoMenuVisible) {
      return;
    }

    this.coverPhotoMenuVisible = false;
    this.syncModeUrl();
  }

  editGroupName() {
    this.editingName = true;
    this.syncModeUrl();
  }

  editGroupDescription() {
    this.editingDescription = true;
    this.syncModeUrl();
  }

  editGroupWebsiteUrl() {
    this.editingWebsiteUrl = true;
    this.syncModeUrl();
  }

  editGroupSport() {
    this.editingSport = true;
    this.syncModeUrl();
  }

  private openPhotoPicker(input: HTMLInputElement) {
    input.value = '';
    input.click();
  }

  private async readSelectedPhoto(event: Event, update: (photoUrl: string, file: File) => void) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const result = await this.photoService.createPreview(file);
    if (result.error) {
      this.showOperationError(result.error);
      this.cdr.markForCheck();
      return;
    }

    if (!result.dataUrl || !result.file) {
      return;
    }

    this.operationErrorMessage = '';
    update(result.dataUrl, result.file);
  }

  uploadCoverPhoto(input: HTMLInputElement) {
    this.coverPhotoMenuVisible = false;
    this.syncModeUrl();
    this.openPhotoPicker(input);
  }

  onCoverPhotoSelected(event: Event) {
    this.readSelectedPhoto(event, (photoUrl, file) => {
      if (!this.editDraft?.id) {
        return;
      }

      this.coverPhotoFailed = false;
      this.setDraftCoverPhoto(this.editDraft, draft => {
        this.editDraft = draft;
      }, photoUrl, file);
      this.syncModeUrl();
      this.cdr.markForCheck();
    });
  }

  removeCoverPhoto() {
    if (!this.editDraft?.id || !this.hasCoverPhoto()) {
      return;
    }

    this.operationErrorMessage = '';
    this.setDraftCoverPhoto(this.editDraft, draft => {
      this.editDraft = draft;
    }, null);
    this.coverPhotoMenuVisible = false;
    this.syncModeUrl();
    this.cdr.markForCheck();
  }

  undoGroupName() {
    this.restoreGroupDraftField('name', () => {
      this.editingName = false;
    });
  }

  undoDescription() {
    this.restoreGroupDraftField('description', () => {
      this.editingDescription = false;
    });
  }

  undoGroupWebsiteUrl() {
    this.restoreGroupDraftField('websiteUrl', () => {
      this.editingWebsiteUrl = false;
    });
  }

  onEditSportChange(value: string) {
    this.editDraft = this.updateDraftSportSelection(this.editDraft, value);
    this.syncModeUrl();
  }

  undoGroupSport() {
    this.restoreGroupDraftField('sport', () => {
      this.editingSport = false;
    });
  }

  discardEditChanges() {
    this.discardDraftChanges(
      this.editOriginal,
      draft => {
        this.editDraft = draft;
      },
      () => {
        this.saveSuccessVisible = false;
        this.discardSuccessVisible = true;
        this.openedInEditMode = false;
        this.editingName = false;
        this.editingDescription = false;
        this.editingWebsiteUrl = false;
        this.editingSport = false;
        this.coverPhotoMenuVisible = false;
      }
    );
    this.syncModeUrl();
    this.scheduleToastDismiss(() => {
      this.discardSuccessVisible = false;
    });
  }

  saveEditChanges() {
    if (!this.editDraft || !this.canSaveEditChanges()) {
      return;
    }
    const savedDraft = this.editDraft;

    const selectedGroupId = this.selectedGroup?.id;
    if (!selectedGroupId) {
      return;
    }

    const sportId = this.getSelectedSportOptionValue(savedDraft.sport);

    this.performMutation(this.groupsApi.updateGroup(selectedGroupId, savedDraft, sportId), () => {
      const cleanDraft = { ...savedDraft, coverPhotoFile: undefined };
      this.rememberSavedCoverPhoto(selectedGroupId, cleanDraft);
      this.editDraft = cleanDraft;
      this.editOriginal = { ...cleanDraft };
      if (this.selectedGroup) {
        this.selectedGroup = {
          ...this.selectedGroup,
          name: cleanDraft.name,
          sport: cleanDraft.sport
        };
      }
      this.refreshUpdatedGroupDetail(selectedGroupId, cleanDraft.coverPhotoUrl);
      this.discardSuccessVisible = false;
      this.saveSuccessVisible = true;
      this.editingName = false;
      this.editingDescription = false;
      this.editingWebsiteUrl = false;
      this.editingSport = false;
      this.openedInEditMode = false;
      this.syncModeUrl();

      this.scheduleToastDismiss(() => {
        this.saveSuccessVisible = false;
      });
    });
  }

  openDeleteFromEditDrawer() {
    if (this.selectedGroup) {
      this.openDeleteDialog(this.selectedGroup);
    } else if (this.editDraft) {
      this.openDeleteDialog(this.createDeleteTarget(this.editDraft));
    }
  }

  toggleMembersExpanded() {
    this.membersExpanded = !this.membersExpanded;
    if (this.membersExpanded) {
      this.loadFullMembersList(this.editDraft);
    }
    this.persistModalSession();
  }


  toggleSubgroupsExpanded() {
    this.subgroupsExpanded = !this.subgroupsExpanded;
    if (this.subgroupsExpanded) {
      this.loadFullSubgroupsList(this.editDraft);
    }
    this.persistModalSession();
  }

  toggleActivitiesExpanded(status: GroupActivityStatusTab) {
    this.groupActivityStatus = status;
    this.activitiesExpanded = !this.activitiesExpanded;
    if (this.activitiesExpanded) {
      this.loadFullActivitiesList(this.editDraft, status);
    }
    this.persistModalSession();
  }

  onActivityStatusSelected(draft: EditGroupDraft | null, status: GroupActivityStatusTab, isSubgroup = false) {
    if (isSubgroup) {
      this.subgroupActivityStatus = status;
      this.subgroupActivitiesExpanded = false;
    } else {
      this.groupActivityStatus = status;
      this.activitiesExpanded = false;
    }

    this.loadActivityStatusPreview(draft, status, true);
    this.persistModalSession();
  }

  dismissDiscardSuccess() {
    this.discardSuccessVisible = false;
  }


  openSubgroupFromList(subgroup: EditGroupSubgroup) {
    this.activityDrawerHost?.close();

    if (this.subgroupDrawerVisible && this.subgroupDraft && this.subgroupOriginal) {
      const frame = this.captureCurrentSubgroupFrame();
      if (frame) {
        this.subgroupDrawerStack.push(frame);
      }
    }

    this.setSubgroupDraft(subgroup);
    this.skipSubgroupDrawerEnterAnimation = this.subgroupDrawerStack.length > 0;
    this.subgroupDrawerClosing = false;
    this.subgroupDrawerVisible = true;
    this.updateModalViewportLock();
    this.syncModeUrl();
  }

  private captureCurrentSubgroupFrame(): SubgroupDrawerStackFrame | null {
    if (!this.subgroupDraft || !this.subgroupOriginal) {
      return null;
    }

    const body = this.document.querySelector<HTMLElement>('[data-groups-drawer-body="subgroup"]');
    if (body) {
      this.subgroupDrawerScrollPosition = body.scrollTop;
    }

    return {
      draft: { ...this.subgroupDraft },
      original: { ...this.subgroupOriginal },
      drawerScrollPosition: this.subgroupDrawerScrollPosition,
      editingName: this.subgroupEditingName,
      editingDescription: this.subgroupEditingDescription,
      editingWebsiteUrl: this.subgroupEditingWebsiteUrl,
      editingSport: this.subgroupEditingSport,
      coverPhotoMenuVisible: this.subgroupCoverPhotoMenuVisible,
      coverPhotoFailed: this.subgroupCoverPhotoFailed,
      saveSuccessVisible: this.subgroupSaveSuccessVisible,
      discardSuccessVisible: this.subgroupDiscardSuccessVisible,
      membersExpanded: this.subgroupMembersExpanded,
      subgroupsExpanded: this.subgroupSubgroupsExpanded,
      activitiesExpanded: this.subgroupActivitiesExpanded
    };
  }

  private restoreSubgroupFrame(frame: SubgroupDrawerStackFrame) {
    this.subgroupDraft = { ...frame.draft };
    this.subgroupOriginal = { ...frame.original };
    this.subgroupDrawerScrollPosition = frame.drawerScrollPosition;
    this.subgroupEditingName = frame.editingName;
    this.subgroupEditingDescription = frame.editingDescription;
    this.subgroupEditingWebsiteUrl = frame.editingWebsiteUrl;
    this.subgroupEditingSport = frame.editingSport;
    this.subgroupCoverPhotoMenuVisible = frame.coverPhotoMenuVisible;
    this.subgroupCoverPhotoFailed = frame.coverPhotoFailed;
    this.subgroupSaveSuccessVisible = frame.saveSuccessVisible;
    this.subgroupDiscardSuccessVisible = frame.discardSuccessVisible;
    this.subgroupMembersExpanded = frame.membersExpanded;
    this.subgroupSubgroupsExpanded = frame.subgroupsExpanded;
    this.subgroupActivitiesExpanded = frame.activitiesExpanded;
    this.cdr.markForCheck();
    this.document.defaultView?.requestAnimationFrame(() => {
      this.restoreDrawerScrollPosition('subgroup');
    });
  }

  private setSubgroupDraft(subgroup: EditGroupSubgroup) {
    this.subgroupDrawerScrollPosition = 0;
    const draft = this.createSubgroupDraftShell(subgroup);

    this.subgroupDraft = { ...draft };
    this.subgroupOriginal = { ...draft };
    this.subgroupEditingName = false;
    this.subgroupEditingDescription = false;
    this.subgroupEditingWebsiteUrl = false;
    this.subgroupEditingSport = false;
    this.subgroupCoverPhotoMenuVisible = false;
    this.subgroupCoverPhotoFailed = false;
    this.subgroupSaveSuccessVisible = false;
    this.subgroupDiscardSuccessVisible = false;
    this.subgroupMembersExpanded = false;
    this.subgroupSubgroupsExpanded = false;
    this.subgroupActivitiesExpanded = false;
    this.initializeReadonlyListsForDraft(this.subgroupDraft);
    this.operationSubscriptions.add(this.groupsApi.getDetail(subgroup.id).subscribe({
      next: detail => {
        if (this.subgroupDraft?.id !== subgroup.id) {
          return;
        }

        this.subgroupDraft = this.applySavedCoverPhotoOverride(subgroup.id, { ...detail.draft, id: subgroup.id });
        this.subgroupOriginal = { ...this.subgroupDraft };
        this.setReadonlyListsForDraft(this.subgroupDraft, detail);
        this.persistModalSession();
        this.cdr.markForCheck();
      },
      error: error => {
        if (this.subgroupDraft?.id === subgroup.id) {
          this.showOperationError(this.getApiErrorMessage(error, 'Unable to load subgroup detail. Please try again.'));
          this.cdr.markForCheck();
        }
      }
    }));
  }

  private syncVisibleGroupsFromState() {
    this.groups = this.groups
      .map(group => this.loadedGroupsSnapshot.find(item => item.id === group.id) ?? group)
      .filter(group => this.loadedGroupsSnapshot.some(item => item.id === group.id));
  }

  closeSubgroupDrawer() {
    const previousFrame = this.subgroupDrawerStack.pop();
    if (previousFrame) {
      this.restoreSubgroupFrame(previousFrame);
      this.skipSubgroupDrawerEnterAnimation = true;
      this.subgroupDrawerClosing = false;
      this.subgroupDrawerVisible = true;
      this.updateModalViewportLock();
      this.syncModeUrl();
      return;
    }

    this.skipSubgroupDrawerEnterAnimation = false;
    this.subgroupDrawerClosing = true;
    this.subgroupDrawerVisible = false;
    this.updateModalViewportLock();
    this.syncModeUrl();
    this.scheduleSubgroupDrawerCloseFallback();
    this.cdr.markForCheck();
  }

  onSubgroupDrawerHidden() {
    if (this.subgroupDrawerVisible) {
      return;
    }

    this.clearSubgroupDrawerCloseFallback();
    this.subgroupDrawerClosing = false;
    this.updateModalViewportLock();
    this.clearSubgroupDrawerState();
    this.activityDrawerHost?.clear();
    if (!this.drawerOnly) {
      this.syncModeUrl();
    }
    this.scheduleIdleModalCleanup();
  }

  editSubgroupName() {
    this.subgroupEditingName = true;
    this.syncModeUrl();
  }

  editSubgroupDescription() {
    this.subgroupEditingDescription = true;
    this.syncModeUrl();
  }

  editSubgroupWebsiteUrl() {
    this.subgroupEditingWebsiteUrl = true;
    this.syncModeUrl();
  }

  editSubgroupSport() {
    this.subgroupEditingSport = true;
    this.syncModeUrl();
  }

  toggleSubgroupMembersExpanded() {
    this.subgroupMembersExpanded = !this.subgroupMembersExpanded;
    if (this.subgroupMembersExpanded) {
      this.loadFullMembersList(this.subgroupDraft);
    }
    this.persistModalSession();
  }

  toggleSubgroupSubgroupsExpanded() {
    this.subgroupSubgroupsExpanded = !this.subgroupSubgroupsExpanded;
    if (this.subgroupSubgroupsExpanded) {
      this.loadFullSubgroupsList(this.subgroupDraft);
    }
    this.persistModalSession();
  }

  toggleSubgroupActivitiesExpanded(status: GroupActivityStatusTab) {
    this.subgroupActivityStatus = status;
    this.subgroupActivitiesExpanded = !this.subgroupActivitiesExpanded;
    if (this.subgroupActivitiesExpanded) {
      this.loadFullActivitiesList(this.subgroupDraft, status);
    }
    this.persistModalSession();
  }

  toggleSubgroupCoverPhotoMenu(event: Event) {
    event.stopPropagation();
    this.subgroupCoverPhotoMenuVisible = !this.subgroupCoverPhotoMenuVisible;
    this.syncModeUrl();
  }

  closeSubgroupCoverPhotoMenu() {
    if (!this.subgroupCoverPhotoMenuVisible) {
      return;
    }

    this.subgroupCoverPhotoMenuVisible = false;
    this.syncModeUrl();
  }

  uploadSubgroupCoverPhoto(input: HTMLInputElement) {
    this.subgroupCoverPhotoMenuVisible = false;
    this.syncModeUrl();
    this.openPhotoPicker(input);
  }

  onSubgroupCoverPhotoSelected(event: Event) {
    this.readSelectedPhoto(event, (photoUrl, file) => {
      if (!this.subgroupDraft?.id) {
        return;
      }

      this.subgroupCoverPhotoFailed = false;
      this.setDraftCoverPhoto(this.subgroupDraft, draft => {
        this.subgroupDraft = draft;
      }, photoUrl, file);
      this.syncModeUrl();
      this.cdr.markForCheck();
    });
  }

  removeSubgroupCoverPhoto() {
    if (!this.subgroupDraft?.id || !this.hasSubgroupCoverPhoto()) {
      return;
    }

    this.operationErrorMessage = '';
    this.setDraftCoverPhoto(this.subgroupDraft, draft => {
      this.subgroupDraft = draft;
    }, null);
    this.subgroupCoverPhotoMenuVisible = false;
    this.syncModeUrl();
    this.cdr.markForCheck();
  }

  undoSubgroupName() {
    this.restoreSubgroupDraftField('name', () => {
      this.subgroupEditingName = false;
    });
  }

  undoSubgroupDescription() {
    this.restoreSubgroupDraftField('description', () => {
      this.subgroupEditingDescription = false;
    });
  }

  undoSubgroupWebsiteUrl() {
    this.restoreSubgroupDraftField('websiteUrl', () => {
      this.subgroupEditingWebsiteUrl = false;
    });
  }

  onSubgroupSportChange(value: string) {
    this.subgroupDraft = this.updateDraftSportSelection(this.subgroupDraft, value);
    this.syncModeUrl();
  }

  undoSubgroupSport() {
    this.restoreSubgroupDraftField('sport', () => {
      this.subgroupEditingSport = false;
    });
  }

  canSaveSubgroupChanges(): boolean {
    return this.canSaveDraftChanges(this.subgroupDraft, this.subgroupOriginal, {
      requireDescription: this.subgroupEditingDescription,
    });
  }

  isSubgroupEditActionMode(): boolean {
    return (
      this.subgroupEditingName ||
      this.subgroupEditingDescription ||
      this.subgroupEditingWebsiteUrl ||
      this.subgroupEditingSport ||
      this.subgroupCoverPhotoMenuVisible ||
      this.hasSubgroupChanges()
    );
  }

  dismissSubgroupSaveSuccess() {
    this.subgroupSaveSuccessVisible = false;
    this.clearToastTimeout();
  }

  dismissSubgroupDiscardSuccess() {
    this.subgroupDiscardSuccessVisible = false;
    this.clearToastTimeout();
  }

  discardSubgroupChanges() {
    this.discardDraftChanges(
      this.subgroupOriginal,
      draft => {
        this.subgroupDraft = draft;
      },
      () => {
        this.subgroupSaveSuccessVisible = false;
        this.subgroupDiscardSuccessVisible = true;
        this.subgroupEditingName = false;
        this.subgroupEditingDescription = false;
        this.subgroupEditingWebsiteUrl = false;
        this.subgroupEditingSport = false;
        this.subgroupCoverPhotoMenuVisible = false;
      }
    );
    this.syncModeUrl();
    this.scheduleToastDismiss(() => {
      this.subgroupDiscardSuccessVisible = false;
    });
  }

  saveSubgroupChanges() {
    if (!this.subgroupDraft || !this.canSaveSubgroupChanges()) {
      return;
    }
    const savedDraft = this.subgroupDraft;
    if (!savedDraft.id) {
      return;
    }
    const subgroupId = savedDraft.id;

    const sportId = this.getSelectedSportOptionValue(savedDraft.sport);

    this.performMutation(this.groupsApi.updateGroup(subgroupId, savedDraft, sportId), () => {
      const cleanDraft = { ...savedDraft, coverPhotoFile: undefined };
      this.rememberSavedCoverPhoto(subgroupId, cleanDraft);
      this.subgroupDraft = cleanDraft;
      this.subgroupOriginal = { ...cleanDraft };
      this.patchSubgroupCardsFromDraft(cleanDraft);
      this.refreshUpdatedSubgroupDetail(subgroupId, cleanDraft.coverPhotoUrl);
      this.subgroupDiscardSuccessVisible = false;
      this.subgroupEditingName = false;
      this.subgroupEditingDescription = false;
      this.subgroupEditingWebsiteUrl = false;
      this.subgroupEditingSport = false;
      this.subgroupSaveSuccessVisible = true;
      this.syncModeUrl();
      this.scheduleToastDismiss(() => {
        this.subgroupSaveSuccessVisible = false;
      });
    }, false);
  }

  private patchSubgroupCardsFromDraft(draft: EditGroupDraft) {
    if (!draft.id) {
      return;
    }

    this.subgroupsByDraftKey.forEach((subgroups, cacheKey) => {
      let didUpdate = false;
      const nextSubgroups = subgroups.map(subgroup => {
        if (subgroup.id !== draft.id) {
          return subgroup;
        }

        didUpdate = true;
        return {
          ...subgroup,
          name: draft.name,
          thumbnailUrl: draft.coverPhotoUrl ?? '',
          sport: draft.sport,
          sportIcon: draft.sportIcon ?? subgroup.sportIcon,
          description: draft.description,
          websiteUrl: draft.websiteUrl
        };
      });

      if (didUpdate) {
        this.subgroupsByDraftKey.set(cacheKey, nextSubgroups);
      }
    });

    this.persistModalSession();
    this.cdr.markForCheck();
  }

  openSubgroupDelete() {
    if (this.subgroupDraft) {
      this.openDeleteDialog(this.createDeleteTarget(this.subgroupDraft), 'subgroup');
    }
  }

  canToggleMembersList(draft: EditGroupDraft | null, expanded: boolean): boolean {
    return canTogglePreviewList(this.getMemberListTotal(draft), expanded, this.editListPreviewCount);
  }

  canToggleSubgroupsList(draft: EditGroupDraft | null, expanded: boolean): boolean {
    return canTogglePreviewList(this.getSubgroupListTotal(draft), expanded, this.subgroupListPreviewCount);
  }

  private getDraftCacheKey(draft: EditGroupDraft | null): string {
    return draft?.id || `${draft?.name ?? 'root'}:${draft?.sport ?? 'all'}`;
  }

  private initializeReadonlyListsForDraft(draft: EditGroupDraft | null) {
    const cacheKey = this.getDraftCacheKey(draft);
    this.membersByDraftKey.set(cacheKey, []);
    this.subgroupsByDraftKey.set(cacheKey, []);
    this.activitiesByDraftKey.set(cacheKey, []);
    this.memberTotalsByDraftKey.set(cacheKey, 0);
    this.subgroupTotalsByDraftKey.set(cacheKey, 0);
    this.activityTotalsByDraftKey.set(cacheKey, 0);
    this.activityStatusCountsByDraftKey.set(cacheKey, this.getEmptyActivityStatusCounts());
    this.fullMembersLoadedByDraftKey.delete(cacheKey);
    this.fullSubgroupsLoadedByDraftKey.delete(cacheKey);
    this.deleteActivityStatusKeys(this.activityListRequestsByDraftKey, cacheKey);
    this.deleteActivityStatusKeys(this.fullActivitiesLoadedByDraftKey, cacheKey);
  }

  private setReadonlyListsForDraft(draft: EditGroupDraft | null, detail: GroupManagementDetail) {
    const cacheKey = this.getDraftCacheKey(draft);
    this.membersByDraftKey.set(cacheKey, detail.members);
    this.subgroupsByDraftKey.set(cacheKey, detail.subgroups);
    this.activitiesByDraftKey.set(cacheKey, detail.activities);
    this.memberTotalsByDraftKey.set(cacheKey, detail.membersTotal);
    this.subgroupTotalsByDraftKey.set(cacheKey, detail.subgroupsTotal);
    this.activityTotalsByDraftKey.set(cacheKey, detail.activitiesTotal);
    this.activityStatusCountsByDraftKey.set(cacheKey, detail.activityStatusCounts);
    this.readonlyListRequestsByDraftKey.add(cacheKey);
    this.toggleFullListLoaded(this.fullMembersLoadedByDraftKey, cacheKey, detail.members.length, detail.membersTotal);
    this.toggleFullListLoaded(this.fullSubgroupsLoadedByDraftKey, cacheKey, detail.subgroups.length, detail.subgroupsTotal);
    this.toggleFullListLoaded(
      this.fullActivitiesLoadedByDraftKey,
      this.getActivityStatusRequestKey(cacheKey, 'incoming'),
      detail.activities.length,
      detail.activityStatusCounts.incoming
    );
  }

  private loadFullMembersList(draft: EditGroupDraft | null, forceReload = false) {
    const groupId = draft?.id;
    if (!groupId) {
      return;
    }

    const cacheKey = this.getDraftCacheKey(draft);
    const total = this.getMemberListTotal(draft);
    if (!forceReload && this.fullMembersLoadedByDraftKey.has(cacheKey)) {
      return;
    }

    if (this.memberListRequestsByDraftKey.has(cacheKey)) {
      return;
    }

    this.memberListRequestsByDraftKey.add(cacheKey);
    this.operationSubscriptions.add(this.groupsApi.getMembers(groupId, 1, this.getReadonlyListLimit(total)).subscribe({
      next: section => {
        this.membersByDraftKey.set(cacheKey, section.data);
        this.memberTotalsByDraftKey.set(cacheKey, section.total);
        this.fullMembersLoadedByDraftKey.add(cacheKey);
        this.memberListRequestsByDraftKey.delete(cacheKey);
        this.persistModalSession();
        this.cdr.markForCheck();
      },
      error: error => {
        this.memberListRequestsByDraftKey.delete(cacheKey);
        this.showOperationError(this.getApiErrorMessage(error, 'Unable to load group members. Please try again.'));
        this.cdr.markForCheck();
      }
    }));
  }

  private loadFullSubgroupsList(draft: EditGroupDraft | null, forceReload = false) {
    const groupId = draft?.id;
    if (!groupId) {
      return;
    }

    const cacheKey = this.getDraftCacheKey(draft);
    const total = this.getSubgroupListTotal(draft);
    if (!forceReload && this.fullSubgroupsLoadedByDraftKey.has(cacheKey)) {
      return;
    }

    if (this.subgroupListRequestsByDraftKey.has(cacheKey)) {
      return;
    }

    this.subgroupListRequestsByDraftKey.add(cacheKey);
    this.operationSubscriptions.add(this.groupsApi.getSubgroups(groupId, 1, this.getReadonlyListLimit(total)).subscribe({
      next: section => {
        this.subgroupsByDraftKey.set(cacheKey, section.data);
        this.subgroupTotalsByDraftKey.set(cacheKey, section.total);
        this.fullSubgroupsLoadedByDraftKey.add(cacheKey);
        this.subgroupListRequestsByDraftKey.delete(cacheKey);
        this.persistModalSession();
        this.cdr.markForCheck();
      },
      error: error => {
        this.subgroupListRequestsByDraftKey.delete(cacheKey);
        this.showOperationError(this.getApiErrorMessage(error, 'Unable to load group subgroups. Please try again.'));
        this.cdr.markForCheck();
      }
    }));
  }

  private loadActivityStatusPreview(draft: EditGroupDraft | null, status: GroupActivityStatusTab, forceReload = false) {
    const groupId = draft?.id;
    if (!groupId) {
      return;
    }

    const cacheKey = this.getDraftCacheKey(draft);
    const requestKey = this.getActivityStatusRequestKey(cacheKey, status);
    if (!forceReload && this.hasLoadedActivityStatusPreview(draft, status)) {
      return;
    }

    if (this.activityListRequestsByDraftKey.has(requestKey)) {
      return;
    }

    this.activityListRequestsByDraftKey.add(requestKey);
    this.operationSubscriptions.add(this.groupsApi.getActivities(groupId, 1, this.activityListPreviewCount, status).subscribe({
      next: section => {
        this.mergeActivitiesForStatus(cacheKey, status, section.data);
        this.fullActivitiesLoadedByDraftKey.delete(requestKey);
        this.activityListRequestsByDraftKey.delete(requestKey);
        this.persistModalSession();
        this.cdr.markForCheck();
      },
      error: error => {
        this.activityListRequestsByDraftKey.delete(requestKey);
        this.showOperationError(this.getApiErrorMessage(error, 'Unable to load group activities. Please try again.'));
        this.cdr.markForCheck();
      }
    }));
  }

  private loadFullActivitiesList(draft: EditGroupDraft | null, status: GroupActivityStatusTab, forceReload = false) {
    const groupId = draft?.id;
    if (!groupId) {
      return;
    }

    const cacheKey = this.getDraftCacheKey(draft);
    const requestKey = this.getActivityStatusRequestKey(cacheKey, status);
    const total = this.getActivityStatusTotal(draft, status);
    if (!forceReload && this.fullActivitiesLoadedByDraftKey.has(requestKey)) {
      return;
    }

    if (this.activityListRequestsByDraftKey.has(requestKey)) {
      return;
    }

    this.activityListRequestsByDraftKey.add(requestKey);
    this.operationSubscriptions.add(this.groupsApi.getActivities(groupId, 1, this.getReadonlyListLimit(total), status).subscribe({
      next: section => {
        this.mergeActivitiesForStatus(cacheKey, status, section.data);
        this.fullActivitiesLoadedByDraftKey.add(requestKey);
        this.activityListRequestsByDraftKey.delete(requestKey);
        this.persistModalSession();
        this.cdr.markForCheck();
      },
      error: error => {
        this.activityListRequestsByDraftKey.delete(requestKey);
        this.showOperationError(this.getApiErrorMessage(error, 'Unable to load group activities. Please try again.'));
        this.cdr.markForCheck();
      }
    }));
  }

  private mergeActivitiesForStatus(cacheKey: string, status: GroupActivityStatusTab, activities: EditGroupActivity[]) {
    const currentActivities = this.activitiesByDraftKey.get(cacheKey) ?? [];
    const nextActivities = [
      ...currentActivities.filter(activity => this.toActivityStatusCountKey(activity.status) !== status),
      ...activities
    ];

    this.activitiesByDraftKey.set(cacheKey, nextActivities);
  }

  private hasLoadedActivityStatusPreview(draft: EditGroupDraft | null, status: GroupActivityStatusTab): boolean {
    const total = this.getActivityStatusTotal(draft, status);
    if (total === 0) {
      return true;
    }

    return this.getComputedActivities(draft)
      .filter(activity => this.toActivityStatusCountKey(activity.status) === status)
      .length > 0;
  }

  private getActivityStatusRequestKey(cacheKey: string, status: GroupActivityStatusTab): string {
    return `${cacheKey}:${status}`;
  }

  private deleteActivityStatusKeys(keys: Set<string>, cacheKey: string) {
    keys.delete(this.getActivityStatusRequestKey(cacheKey, 'incoming'));
    keys.delete(this.getActivityStatusRequestKey(cacheKey, 'completed'));
    keys.delete(this.getActivityStatusRequestKey(cacheKey, 'cancelled'));
  }

  private getReadonlyListLimit(total: number): number {
    return Math.max(total, this.editListPreviewCount, 10);
  }

  private toggleFullListLoaded(loadedKeys: Set<string>, cacheKey: string, count: number, total: number) {
    if (total > 0 && count >= total) {
      loadedKeys.add(cacheKey);
      return;
    }

    loadedKeys.delete(cacheKey);
  }

  getComputedMembers(draft: EditGroupDraft | null): EditGroupMember[] {
    return this.membersByDraftKey.get(this.getDraftCacheKey(draft)) ?? [];
  }

  getComputedSubgroups(draft: EditGroupDraft | null): EditGroupSubgroup[] {
    return this.subgroupsByDraftKey.get(this.getDraftCacheKey(draft)) ?? [];
  }

  getVisibleMembers(draft: EditGroupDraft | null, expanded: boolean): EditGroupMember[] {
    return getPreviewItems(this.getComputedMembers(draft), expanded, this.editListPreviewCount);
  }

  getVisibleSubgroups(draft: EditGroupDraft | null, expanded: boolean): EditGroupSubgroup[] {
    return getPreviewItems(this.getComputedSubgroups(draft), expanded, this.subgroupListPreviewCount);
  }

  getMemberListTotal(draft: EditGroupDraft | null): number {
    return this.memberTotalsByDraftKey.get(this.getDraftCacheKey(draft)) ?? this.getComputedMembers(draft).length;
  }

  getSubgroupListTotal(draft: EditGroupDraft | null): number {
    return this.subgroupTotalsByDraftKey.get(this.getDraftCacheKey(draft)) ?? this.getComputedSubgroups(draft).length;
  }

  getMembersMoreCount(draft: EditGroupDraft | null, expanded: boolean): number {
    return getPreviewMoreCount(this.getMemberListTotal(draft), expanded, this.editListPreviewCount);
  }

  getSubgroupsMoreCount(draft: EditGroupDraft | null, expanded: boolean): number {
    return getPreviewMoreCount(this.getSubgroupListTotal(draft), expanded, this.subgroupListPreviewCount);
  }

  getComputedActivities(draft: EditGroupDraft | null): EditGroupActivity[] {
    return this.activitiesByDraftKey.get(this.getDraftCacheKey(draft)) ?? [];
  }

  getVisibleActivities(draft: EditGroupDraft | null, expanded: boolean): EditGroupActivity[] {
    return getPreviewItems(this.getComputedActivities(draft), expanded, this.activityListPreviewCount);
  }

  getActivityListTotal(draft: EditGroupDraft | null): number {
    return this.activityTotalsByDraftKey.get(this.getDraftCacheKey(draft)) ?? this.getComputedActivities(draft).length;
  }

  getActivityStatusCounts(draft: EditGroupDraft | null): ActivityStatusCounts {
    return this.activityStatusCountsByDraftKey.get(this.getDraftCacheKey(draft))
      ?? this.countActivitiesByStatus(this.getComputedActivities(draft));
  }

  private getActivityStatusTotal(draft: EditGroupDraft | null, status: GroupActivityStatusTab): number {
    return this.getActivityStatusCounts(draft)[status] ?? 0;
  }

  private getEmptyActivityStatusCounts(): ActivityStatusCounts {
    return {
      incoming: 0,
      completed: 0,
      cancelled: 0
    };
  }

  private countActivitiesByStatus(activities: EditGroupActivity[]): ActivityStatusCounts {
    const counts = this.getEmptyActivityStatusCounts();

    for (const activity of activities) {
      const status = this.toActivityStatusCountKey(activity.status);
      counts[status] += 1;
    }

    return counts;
  }

  private toActivityStatusCountKey(status?: string): keyof ActivityStatusCounts {
    const normalizedStatus = status?.trim().toLowerCase();
    switch (normalizedStatus) {
      case 'scheduled':
        return 'incoming';
      case 'finished':
        return 'completed';
      case 'cancelled':
        return 'cancelled';
      default:
        return 'incoming';
    }
  }

  getActivitiesMoreCount(draft: EditGroupDraft | null, expanded: boolean): number {
    return getPreviewMoreCount(this.getActivityListTotal(draft), expanded, this.activityListPreviewCount);
  }

  canToggleActivitiesList(draft: EditGroupDraft | null, expanded: boolean): boolean {
    return canTogglePreviewList(this.getActivityListTotal(draft), expanded, this.activityListPreviewCount);
  }

  openActivityFromList(activity: EditGroupActivity) {
    this.activityDrawerHost?.open(activity);
    this.updateModalViewportLock();
    this.syncModeUrl();
    this.cdr.markForCheck();
  }

  onActivityDrawerShown() {
    this.restoreDrawerScrollPosition('activity', true);
  }

  onActivityVisibilityChange(visible: boolean) {
    if (visible) {
      this.onActivityDrawerShown();
    }
    this.updateModalViewportLock();
    this.syncModeUrl();
    if (!visible) {
      this.scheduleIdleModalCleanup();
      queueMicrotask(() => {
        this.syncGroupDrawerOutsideDismiss();
        this.cdr.markForCheck();
      });
    }
    this.cdr.markForCheck();
  }

  private scheduleIdleModalCleanup() {
    this.document.defaultView?.requestAnimationFrame(() => {
      this.cleanupIdleModalArtifacts();
      this.document.defaultView?.setTimeout(() => this.cleanupIdleModalArtifacts(), 180);
    });
  }

  private cleanupIdleModalArtifacts() {
    if (this.isAnyModalVisible()) {
      return;
    }

    this.unlockModalViewport();
    this.document.body.classList.remove('groups-modal-background-locked');
    this.document.body.style.removeProperty('--groups-modal-scroll-top');
    this.document.removeEventListener('wheel', this.preventBackgroundPointerScroll, true);
    this.document.removeEventListener('touchmove', this.preventBackgroundPointerScroll, true);
    this.document.removeEventListener('keydown', this.preventBackgroundKeyboardScroll, true);

    if (this.document.querySelector('.p-sidebar-active')) {
      return;
    }

    this.document.querySelectorAll<HTMLElement>('body > .p-sidebar-mask').forEach(mask => {
      mask.remove();
    });
  }

  private deleteActivity() {
    if (!this.deleteConfirmed) {
      return;
    }

    this.activityDrawerHost?.confirmDeleteFromParent();
  }
}
